<template>
  <div class="address-monitoring-page" :class="{ 'address-monitoring-page--embedded': embedded }">
    <header class="address-monitoring-page__header">
      <div class="address-monitoring-page__hero-copy">
        <p class="address-monitoring-page__eyebrow">Case Buddy companion</p>
        <h1>Address Monitoring</h1>
        <p class="address-monitoring-page__subtitle" v-if="!embedded">
          Check saved dormant/path-ending items with Tool 88. The page starts quiet: select an item, review the basis, then run a check when it is due.
        </p>
        <p class="address-monitoring-page__subtitle" v-else>
          Embedded current-case view. The full monitor remains cross-case and can be opened separately.
        </p>
        <div class="address-monitoring-page__chips">
          <span :class="['address-monitoring-page__session-chip', session.isAuthenticated ? 'is-authenticated' : 'is-guest']">{{ sessionLabel }}</span>
          <span class="address-monitoring-page__quiet-chip">{{ notificationLabel }}</span>
          <span v-if="lastLoadedAt" class="address-monitoring-page__quiet-chip">Updated {{ formatDateTime(lastLoadedAt) }}</span>
        </div>
      </div>

      <div class="address-monitoring-page__actions">
        <label v-if="embedded && caseFilterLabel" class="address-monitoring-page__embedded-filter">
          <input v-model="caseFilterEnabled" type="checkbox" />
          <span>{{ caseFilterLabel }}</span>
        </label>
        <a v-if="embedded" class="address-monitoring-page__button-link" href="/monitoring" target="_blank" rel="noopener">Open full page</a>
        <button v-if="embedded" type="button" class="secondary" @click="$emit('close')">Close</button>
        <button type="button" @click="loadItems" :disabled="loading">{{ loading ? 'Refreshing…' : 'Refresh' }}</button>
        <button type="button" class="primary" @click="checkAllDue" :disabled="isRunActive || !dueItems.length">
          <template v-if="batchRun.active">Checking queue…</template>
          <template v-else>Check due<span v-if="dueItems.length"> · {{ dueItems.length }}</span></template>
        </button>
        <button type="button" @click="openUtility('help')">Help & settings</button>
      </div>
    </header>

    <section v-if="feedback" :class="['address-monitoring-page__feedback', `is-${feedbackTone}`]">
      {{ feedback }}
    </section>

    <section v-if="batchRun.active" class="address-monitoring-page__batch-strip" role="status" aria-live="polite">
      <div class="address-monitoring-page__batch-spinner" aria-hidden="true"><span></span><span></span></div>
      <div class="address-monitoring-page__batch-copy">
        <p class="address-monitoring-page__eyebrow">Queued Tool 88 checks</p>
        <h2>{{ batchStatusTitle }}</h2>
        <p>{{ batchStatusMessage }}</p>
        <div class="address-monitoring-page__batch-progress" aria-hidden="true">
          <span :style="{ width: batchProgressPercent + '%' }"></span>
        </div>
      </div>
      <div class="address-monitoring-page__batch-count">
        <strong>{{ batchRun.completed + batchRun.failed + batchRun.skipped }}</strong>
        <span>of {{ batchRun.total }}</span>
      </div>
    </section>

    <section
      v-if="unacknowledgedMovementItems.length"
      class="address-monitoring-page__movement-alert-strip"
      aria-live="polite"
    >
      <div class="address-monitoring-page__movement-alert-icon" aria-hidden="true">!</div>
      <div>
        <p class="address-monitoring-page__eyebrow">Outstanding movement alert</p>
        <h2>{{ unacknowledgedMovementItems.length }} movement alert{{ unacknowledgedMovementItems.length === 1 ? '' : 's' }} need acknowledgement</h2>
        <p>Review the Tool 88 result, copy or act on the movement details, then acknowledge the alert to return the item to normal list order.</p>
      </div>
      <div class="address-monitoring-page__result-actions">
        <button type="button" class="primary" @click="reviewFirstMovementAlert">Review first alert</button>
        <button type="button" @click="setStatusFilter('movement_unacknowledged')">Show alerts</button>
      </div>
    </section>

    <section v-if="lastRunResult" :class="['address-monitoring-page__result-banner', `is-${lastRunTone}`]" aria-live="polite">
      <div>
        <p class="address-monitoring-page__eyebrow">Last completed check</p>
        <h2>{{ formatStatus(lastRunResult.check_status || lastRunResult.status) }}</h2>
        <p>{{ lastRunResult.summary || lastRunResult.error_message || 'The monitoring check finished.' }}</p>
        <p v-if="lastRunResult.notification_sent" class="address-monitoring-page__result-note">Discord alert sent.</p>
        <p v-else-if="lastRunResult.notification_error" class="address-monitoring-page__result-note">Discord alert not sent: {{ lastRunResult.notification_error }}</p>
      </div>
      <div class="address-monitoring-page__result-actions">
        <button type="button" @click="focusLastRunItem">View item</button>
        <button type="button" @click="openSelectedHistory">Open history</button>
        <button type="button" @click="lastRunResult = null">Dismiss</button>
      </div>
    </section>

    <section class="address-monitoring-page__summary-strip" aria-label="Monitoring summary">
      <article>
        <span>{{ embedded && caseFilterEnabled ? 'Current case' : 'Total monitored' }}</span>
        <strong>{{ caseFilteredItems.length }}</strong>
      </article>
      <article :class="{ 'has-action': dueItems.length }">
        <span>Due now</span>
        <strong>{{ dueItems.length }}</strong>
      </article>
      <article :class="{ 'has-alert': unacknowledgedMovementItems.length }">
        <span>Movement alerts</span>
        <strong>{{ unacknowledgedMovementItems.length }}</strong>
        <small v-if="acknowledgedMovementItems.length">{{ acknowledgedMovementItems.length }} acknowledged</small>
      </article>
      <article>
        <span>Needs review</span>
        <strong>{{ reviewAttentionTotal }}</strong>
      </article>
    </section>

    <section class="address-monitoring-page__quick-bar">
      <input v-model.trim="searchQuery" placeholder="Search case, hash, address, token…" />
      <button type="button" @click="showFilters = !showFilters">{{ showFilters ? 'Hide filters' : 'Filters' }}</button>
      <button v-if="searchQuery || statusFilter !== 'all'" type="button" @click="clearFilters">Clear</button>
    </section>

    <section v-if="showFilters" class="address-monitoring-page__filters">
      <label>
        <span>Status</span>
        <select v-model="statusFilter">
          <option value="all">All statuses</option>
          <option value="due">Due now</option>
          <option value="dormant">Dormant</option>
          <option value="movement_unacknowledged">Outstanding movement alerts</option>
          <option value="movement_acknowledged">Acknowledged movement</option>
          <option value="movement_any">Any movement found</option>
          <option value="movement_found">Movement found</option>
          <option value="partial_movement_found">Partial movement</option>
          <option value="review_attention">Needs review or error</option>
          <option value="needs_review">Needs review</option>
          <option value="error">Error</option>
          <option value="disabled">Stopped</option>
        </select>
      </label>
      <label>
        <span>Sort</span>
        <select v-model="sortMode">
          <option value="due_asc">Next due first</option>
          <option value="last_checked_desc">Recently checked</option>
          <option value="created_desc">Newest added</option>
          <option value="case_asc">Case A-Z</option>
          <option value="amount_desc">Amount high-low</option>
          <option value="status_asc">Status</option>
        </select>
      </label>
      <button type="button" @click="openUtility('notifications')">Discord alerts</button>
      <button v-if="canManualAdd" type="button" @click="showManualAdd = !showManualAdd">{{ showManualAdd ? 'Hide manual add' : 'Manual add' }}</button>
      <button type="button" @click="refreshSession" :disabled="sessionLoading">{{ sessionLoading ? 'Checking session…' : 'Refresh session' }}</button>
    </section>

    <section v-if="canManualAdd && showManualAdd" class="address-monitoring-page__manual-panel">
      <div class="address-monitoring-page__section-header">
        <div>
          <p class="address-monitoring-page__eyebrow">Admin/manual entry</p>
          <h2>Add a monitoring item manually</h2>
          <p>This stays hidden by default so the main monitor view remains calm. Enter transaction times in UTC.</p>
        </div>
        <button type="button" @click="resetManualAddForm" :disabled="manualAddSaving">Reset</button>
      </div>

      <div class="address-monitoring-page__manual-tsv-card">
        <div>
          <h3>Paste a Case Buddy row</h3>
          <p>Paste one TSV row copied from the Case Buddy spreadsheet to fill the manual form. Case name stays optional.</p>
        </div>
        <label>
          <span>Copied spreadsheet row</span>
          <textarea
            v-model="manualTsv.rowText"
            rows="4"
            placeholder="Paste one A–X Case Buddy row here. Column B hash, D address, M funds followed, G token, R/S left bounds."
            @paste.prevent="handleManualTsvPaste"
          ></textarea>
        </label>
        <div class="address-monitoring-page__manual-tsv-actions">
          <button type="button" @click="parseManualTsvFromTextarea">Fill form from pasted row</button>
          <button type="button" @click="clearManualTsvPaste">Clear paste</button>
        </div>
        <p v-if="manualTsv.message" :class="['address-monitoring-page__mini-feedback', `is-${manualTsv.tone}`]">{{ manualTsv.message }}</p>
        <div v-if="manualTsv.lastParsed" class="address-monitoring-page__manual-tsv-summary">
          <span><strong>Hash</strong> {{ shortHash(manualTsv.lastParsed.transaction_hash) }}</span>
          <span><strong>Address</strong> {{ shortHash(manualTsv.lastParsed.monitor_address) }}</span>
          <span><strong>Amount</strong> {{ manualTsv.lastParsed.monitor_funds_amount }} {{ manualTsv.lastParsed.token_symbol }}</span>
          <span><strong>Left bounds</strong> {{ manualTsv.lastParsed.left_bounds_amount || '0' }}</span>
        </div>
      </div>

      <div class="address-monitoring-page__manual-layout">
        <form class="address-monitoring-page__manual-form" @submit.prevent="submitManualAdd">
          <label><span>Label</span><input v-model.trim="manualAdd.label" placeholder="Optional display label" /></label>
          <label><span>Case name</span><input v-model.trim="manualAdd.case_name" placeholder="Optional source case" /></label>
          <label><span>Path label</span><input v-model.trim="manualAdd.source_path_label" placeholder="1" /></label>
          <label><span>Transaction hash *</span><input v-model.trim="manualAdd.transaction_hash" autocomplete="off" required /></label>
          <label><span>From address</span><input v-model.trim="manualAdd.from_address" autocomplete="off" /></label>
          <label><span>Monitor / To address *</span><input v-model.trim="manualAdd.monitor_address" autocomplete="off" required /></label>
          <label><span>Transaction UTC</span><input v-model.trim="manualAdd.transaction_utc" type="datetime-local" /></label>
          <label><span>Transaction amount</span><input v-model.trim="manualAdd.transaction_amount" inputmode="decimal" placeholder="Optional" /></label>
          <label><span>Monitor funds amount *</span><input v-model.trim="manualAdd.monitor_funds_amount" inputmode="decimal" required /></label>
          <label><span>Token *</span><input v-model.trim="manualAdd.token_symbol" placeholder="USDT" required /></label>
          <label><span>Left bounds</span><input v-model.trim="manualAdd.left_bounds_amount" inputmode="decimal" placeholder="0" /></label>
          <label><span>Prior balance</span><input v-model.trim="manualAdd.prior_balance_amount" inputmode="decimal" placeholder="Optional" /></label>
          <label><span>Chain ID</span><input v-model.trim="manualAdd.chain_id" placeholder="1" /></label>
          <label><span>Chain name</span><input v-model.trim="manualAdd.chain_name" placeholder="Ethereum" /></label>
          <label><span>Hop logic</span><select v-model="manualAdd.hop_logic"><option value="FIFO">FIFO</option><option value="LIFO">LIFO</option></select></label>
          <label><span>From hash</span><input v-model.trim="manualAdd.from_hash" autocomplete="off" placeholder="Optional parent/from hash" /></label>
          <label class="address-monitoring-page__manual-wide"><span>Notes</span><textarea v-model.trim="manualAdd.user_note" rows="3" placeholder="Optional analyst/admin note"></textarea></label>
          <div class="address-monitoring-page__manual-actions">
            <button type="submit" class="primary" :disabled="manualAddSaving">{{ manualAddSaving ? 'Saving…' : 'Save manual monitor item' }}</button>
            <button type="button" @click="showManualAdd = false" :disabled="manualAddSaving">Cancel</button>
          </div>
        </form>

        <aside class="address-monitoring-page__manual-help">
          <h3>Manual add checklist</h3>
          <p>Use this only when a Case Buddy row did not create the item but the transaction, monitor address, amount, token, chain, and hop logic are known.</p>
          <details>
            <summary>Preview generated A-X row</summary>
            <pre>{{ JSON.stringify(manualPreviewRow, null, 2) }}</pre>
          </details>
        </aside>
      </div>
    </section>

    <section class="address-monitoring-page__layout">
      <div class="address-monitoring-page__list-panel">
        <div class="address-monitoring-page__list-meta">
          <span>{{ filteredItems.length }} shown from {{ caseFilteredItems.length }} in scope</span>
          <span v-if="activeFilterLabel">{{ activeFilterLabel }}</span>
        </div>

        <div v-if="filteredItems.length" class="address-monitoring-page__item-list">
          <button
            v-for="item in filteredItems"
            :key="item.id"
            type="button"
            class="address-monitor-card"
            :class="{
              selected: selectedItem?.id === item.id,
              'is-due': isItemDue(item),
              'is-unacknowledged-movement': isUnacknowledgedMovement(item),
              'is-acknowledged-movement': isAcknowledgedMovement(item),
            }"
            @click="selectItem(item)"
          >
            <span class="address-monitor-card__topline">
              <span :class="['address-monitor-card__status', `is-${item.status}`]">{{ movementStatusLabel(item) }}</span>
              <span v-if="isUnacknowledgedMovement(item)" class="address-monitor-card__alert-chip">Needs acknowledgement</span>
              <span v-else-if="isAcknowledgedMovement(item)" class="address-monitor-card__ack-chip">Acknowledged</span>
              <span v-if="isItemDue(item)" class="address-monitor-card__due">Due</span>
            </span>
            <strong>{{ item.label || item.case_name || 'Dormant funds item' }}</strong>
            <code>{{ shortHash(item.transaction_hash) }} → {{ shortHash(item.monitor_address) }}</code>
            <span>{{ formatAmount(item.monitor_funds_amount, item.token_symbol) }} · {{ item.chain_name || item.chain_id || 'Unknown chain' }}</span>
            <small>{{ nextCheckLabel(item) }}</small>
          </button>
        </div>

        <div v-else class="address-monitoring-page__empty">
          <h3>{{ emptyStateTitle }}</h3>
          <p>{{ emptyStateMessage }}</p>
          <button v-if="searchQuery || statusFilter !== 'all'" type="button" @click="clearFilters">Clear filters</button>
          <button v-else type="button" @click="openUtility('help', 'workflow')">Read how to add items</button>
        </div>
      </div>

      <aside class="address-monitoring-page__detail-panel">
        <template v-if="selectedItem">
          <div class="address-monitoring-page__detail-header">
            <div>
              <p class="address-monitoring-page__eyebrow">Selected monitor</p>
              <h2>{{ selectedItem.label || selectedItem.case_name || 'Dormant funds item' }}</h2>
              <p>{{ selectedItem.case_name || 'No case label' }}<template v-if="selectedItem.source_path_label"> · path {{ selectedItem.source_path_label }}</template></p>
            </div>
            <div class="address-monitoring-page__detail-status-stack">
              <span :class="['address-monitor-card__status', `is-${selectedItem.status}`]">{{ movementStatusLabel(selectedItem) }}</span>
              <span v-if="isUnacknowledgedMovement(selectedItem)" class="address-monitor-card__alert-chip">Needs acknowledgement</span>
              <span v-else-if="isAcknowledgedMovement(selectedItem)" class="address-monitor-card__ack-chip">Acknowledged</span>
            </div>
          </div>

          <section :class="['address-monitoring-page__latest-card', `is-${selectedLastResultTone}`]">
            <strong>{{ selectedStatusExplanation.title }}</strong>
            <p>{{ selectedItem.last_summary || selectedStatusExplanation.body }}</p>
            <p v-if="isAcknowledgedMovement(selectedItem)" class="address-monitoring-page__ack-line">
              Alert acknowledged {{ formatDateTime(alertAcknowledgedAt(selectedItem)) }}. This movement finding is kept for records but no longer floats to the top.
            </p>
            <p v-else-if="isUnacknowledgedMovement(selectedItem)" class="address-monitoring-page__ack-line strong">
              This movement finding is still outstanding. Acknowledge it after you have reviewed or transferred the result into the case workflow.
            </p>
            <p v-if="selectedItem.last_movement_hash" class="address-monitoring-page__movement-line">
              Movement: <code>{{ selectedItem.last_movement_hash }}</code>
              <template v-if="selectedItem.last_movement_to_address"> → <code>{{ selectedItem.last_movement_to_address }}</code></template>
              <template v-if="selectedItem.last_movement_utc"> at {{ formatDateTime(selectedItem.last_movement_utc) }}</template>
            </p>
          </section>

          <div class="address-monitoring-page__primary-check-row">
            <button type="button" class="primary xl" @click="checkItem(selectedItem)" :disabled="isRunActive || !isItemDue(selectedItem)">
              {{ isItemDue(selectedItem) ? 'Check with Tool 88' : 'Cooldown active' }}
            </button>
            <span>{{ nextCheckLabel(selectedItem) }}</span>
          </div>

          <dl class="address-monitoring-page__detail-grid">
            <div><dt>Amount monitored</dt><dd>{{ formatAmount(selectedItem.monitor_funds_amount, selectedItem.token_symbol) }}</dd></div>
            <div><dt>Chain</dt><dd>{{ selectedItem.chain_name || selectedItem.chain_id || 'Unknown' }}</dd></div>
            <div><dt>Transaction</dt><dd><code>{{ selectedItem.transaction_hash }}</code></dd></div>
            <div><dt>Monitor address</dt><dd><code>{{ selectedItem.monitor_address }}</code></dd></div>
            <div><dt>Hop logic</dt><dd>{{ selectedItem.hop_logic || 'FIFO' }}</dd></div>
            <div><dt>Last checked</dt><dd>{{ selectedItem.last_checked_at_utc || selectedItem.last_checked_at ? formatDateTime(selectedItem.last_checked_at_utc || selectedItem.last_checked_at) : 'Never' }}</dd></div>
          </dl>

          <div class="address-monitoring-page__secondary-actions">
            <button
              v-if="isUnacknowledgedMovement(selectedItem)"
              type="button"
              class="primary"
              @click="acknowledgeSelectedMovementAlert"
              :disabled="acknowledgementSaving || isItemActivelyChecking(selectedItem)"
            >
              {{ acknowledgementSaving ? 'Saving…' : isItemActivelyChecking(selectedItem) ? 'Checking this item…' : 'Mark reviewed / dismiss alert' }}
            </button>
            <button
              v-else-if="isAcknowledgedMovement(selectedItem)"
              type="button"
              @click="reopenSelectedMovementAlert"
              :disabled="acknowledgementSaving || isItemActivelyChecking(selectedItem)"
            >
              {{ isItemActivelyChecking(selectedItem) ? 'Checking this item…' : 'Reopen alert' }}
            </button>
            <button type="button" @click="toggleHistory" :disabled="historyLoading">{{ historyOpen ? 'Hide history' : 'History' }}</button>
            <button type="button" @click="copySelectedSummary">Copy summary</button>
            <button v-if="selectedItem.status === 'disabled' || !isMovementStatus(selectedItem)" type="button" @click="toggleSelectedDisabled" :disabled="checkingAny">{{ selectedItem.status === 'disabled' ? 'Resume monitoring' : 'Stop monitoring' }}</button>
            <button type="button" class="danger" @click="deleteSelected" :disabled="checkingAny">Delete</button>
          </div>

          <details class="address-monitoring-page__basis-card">
            <summary>Monitoring basis and stored source row</summary>
            <dl class="address-monitoring-page__basis-grid">
              <div><dt>Left bounds</dt><dd>{{ formatAmount(selectedItem.left_bounds_amount, selectedItem.token_symbol) }}</dd></div>
              <div><dt>Monitor after</dt><dd>{{ selectedItem.monitor_after_utc ? formatDateTime(selectedItem.monitor_after_utc) : 'Transaction time / first check' }}</dd></div>
              <div><dt>Created from</dt><dd>{{ formatCreatedFrom(selectedItem.created_from) }}</dd></div>
              <div><dt>Monitor kind</dt><dd>{{ formatMonitorKind(selectedItem.monitor_kind) }}</dd></div>
            </dl>
            <pre v-if="selectedItem.source_row_json || canManualAdd">{{ JSON.stringify(selectedItem.source_row_json || buildTool88RowsForMonitorItem(selectedItem)[0], null, 2) }}</pre>
          </details>

          <section v-if="historyOpen" class="address-monitoring-page__history-card">
            <div class="address-monitoring-page__section-header compact">
              <div>
                <p class="address-monitoring-page__eyebrow">Check history</p>
                <h3>Recent monitoring runs</h3>
              </div>
              <button type="button" @click="loadHistory(selectedItem, true)" :disabled="historyLoading">{{ historyLoading ? 'Loading…' : 'Reload' }}</button>
            </div>
            <div v-if="historyLoading" class="address-monitoring-page__empty">Loading history…</div>
            <ol v-else-if="historyItems.length" class="address-monitoring-page__history-list">
              <li v-for="check in historyItems" :key="check.id || `${check.started_at_utc}-${check.status}`">
                <span :class="['address-monitor-card__status', `is-${check.status}`]">{{ formatStatus(check.status) }}</span>
                <div>
                  <strong>{{ check.summary || check.error_message || 'Monitoring check' }}</strong>
                  <small>{{ formatDateTime(check.finished_at_utc || check.started_at_utc || check.created_at_utc) }}<template v-if="check.movement_hash"> · movement {{ shortHash(check.movement_hash) }}</template></small>
                </div>
              </li>
            </ol>
            <div v-else class="address-monitoring-page__empty">No check history was returned for this item yet.</div>
          </section>

          <details v-if="canManualAdd" class="address-monitoring-page__debug">
            <summary>Debug / raw result</summary>
            <pre>{{ JSON.stringify(selectedItem.last_result_json || {}, null, 2) }}</pre>
          </details>
        </template>

        <div v-else class="address-monitoring-page__empty">
          <h3>Select a monitoring item</h3>
          <p>Choose an item to review the basis and run Tool 88.</p>
        </div>
      </aside>
    </section>

    <div v-if="utilityOpen" class="address-monitoring-page__side-backdrop" @click.self="closeUtility">
      <aside class="address-monitoring-page__side-panel" role="dialog" aria-modal="true" aria-label="Address monitoring help and settings">
        <div class="address-monitoring-page__side-header">
          <div>
            <p class="address-monitoring-page__eyebrow">Help & settings</p>
            <h2>Address Monitoring</h2>
          </div>
          <button type="button" @click="closeUtility">Close</button>
        </div>
        <div class="address-monitoring-page__side-tabs">
          <button type="button" :class="{ active: utilityTab === 'help' }" @click="utilityTab = 'help'">How it works</button>
          <button type="button" :class="{ active: utilityTab === 'notifications' }" @click="openUtility('notifications')">Discord alerts</button>
          <button type="button" :class="{ active: utilityTab === 'advanced' }" @click="utilityTab = 'advanced'">Advanced</button>
        </div>

        <section v-if="utilityTab === 'help'" class="address-monitoring-page__guide-content">
          <div class="address-monitoring-page__guide-tabs" role="tablist" aria-label="Address monitoring guide sections">
            <button v-for="tab in guideTabs" :key="tab.key" type="button" :class="{ active: guideTab === tab.key }" @click="guideTab = tab.key">{{ tab.label }}</button>
          </div>
          <div v-if="guideTab === 'overview'">
            <details open><summary>What this tool watches</summary><p>Each item stores a transaction hash, monitored receiving address, amount/token, chain, hop logic, and the Case Buddy row snapshot used for the request.</p></details>
            <details><summary>What it does not replace</summary><p>The monitor stores a snapshot and result history. The actual case source remains Case Buddy’s spreadsheet, raw data, address legend, and metadata.</p></details>
            <details><summary>How checks run</summary><p>Checks are manual. Tool 88 is asked whether movement appears after the monitored transaction or cutoff, and the monitor stores the result.</p></details>
          </div>
          <div v-else-if="guideTab === 'workflow'">
            <details open><summary>1. Add an item</summary><p>Normal users add eligible rows from Case Buddy. Admins can also use manual add or legacy Tool 61 imports.</p></details>
            <details open><summary>2. Review the basis</summary><p>Confirm transaction, address, funds, left bounds, chain, and hop logic before checking.</p></details>
            <details open><summary>3. Run Tool 88</summary><p>The monitor builds a one-item Case Buddy-style input and stops after the one-hop monitoring question is answered.</p></details>
            <details open><summary>4. Handle the result</summary><p>No movement leaves the item dormant. Movement, partial movement, error, or needs review should be reviewed before report use.</p></details>
          </div>
          <div v-else-if="guideTab === 'statuses'" class="address-monitoring-page__status-guide">
            <article v-for="status in statusGuide" :key="status.key"><span :class="['address-monitor-card__status', `is-${status.key}`]">{{ status.label }}</span><p>{{ status.description }}</p></article>
          </div>
          <div v-else>
            <details open><summary>Standalone page</summary><p>Best for cross-case review, search, history, notification settings, and batch due checks.</p></details>
            <details open><summary>Embedded in Case Buddy</summary><p>Can stay scoped to the active case while still using the same user-scoped backend monitor.</p></details>
          </div>
        </section>

        <section v-else-if="utilityTab === 'notifications'" class="address-monitoring-page__notification-panel">
          <h3>Discord movement alerts</h3>
          <p>Paste a Discord webhook URL to receive an announcement when a check records movement or partial movement. Channel members who can see that Discord channel can see the alert contents.</p>
          <div class="address-monitoring-page__notification-state">
            <strong>{{ notificationSettings.configured ? 'Webhook saved' : 'No webhook saved' }}</strong>
            <span>{{ notificationSettings.configured ? notificationSettings.masked_target : 'Alerts are off until you save a webhook.' }}</span>
          </div>
          <label>
            <span>Discord webhook URL</span>
            <input v-model.trim="notificationFormUrl" placeholder="https://discord.com/api/webhooks/..." autocomplete="off" />
          </label>
          <div class="address-monitoring-page__secondary-actions">
            <button type="button" class="primary" @click="saveDiscordWebhook" :disabled="notificationSaving">{{ notificationSaving ? 'Saving…' : 'Save webhook' }}</button>
            <button type="button" @click="loadNotificationSettings(true)" :disabled="notificationLoading">Refresh</button>
            <button v-if="notificationSettings.configured" type="button" class="danger" @click="removeDiscordWebhook" :disabled="notificationSaving">Remove webhook</button>
          </div>
          <p v-if="notificationFeedback" :class="['address-monitoring-page__mini-feedback', `is-${notificationTone}`]">{{ notificationFeedback }}</p>
        </section>

        <section v-else class="address-monitoring-page__advanced-panel">
          <button type="button" @click="openFiltersFromUtility">Show filters</button>
          <button v-if="canManualAdd" type="button" @click="openManualAddFromUtility">Open manual add</button>
          <button type="button" @click="refreshSession">Refresh session</button>
          <p v-if="canManualAdd">Manual add and debug drawers are enabled for this admin/mod/can-use-all-tools account.</p>
          <p v-else>Manual add is restricted to admin/mod/can-use-all-tools accounts.</p>
        </section>
      </aside>
    </div>

    <div v-if="isRunActive && runVisualMode === 'modal'" class="address-monitoring-page__checking-overlay" role="status" aria-live="assertive">
      <div class="address-monitoring-page__checking-card">
        <div class="address-monitoring-page__checking-card-actions">
          <button type="button" @click="minimizeRunVisual">Minimize and keep working</button>
        </div>
        <div class="address-monitoring-page__orbit" aria-hidden="true"><span></span><span></span><span></span></div>
        <p class="address-monitoring-page__eyebrow">{{ checkOverlayEyebrow }}</p>
        <h2>{{ checkOverlayTitle }}</h2>
        <p>{{ checkOverlayMessage }}</p>
        <strong>{{ checkingItemLabel }}</strong>
        <p v-if="batchRun.active" class="address-monitoring-page__batch-inline">{{ batchInlineStatus }}</p>
        <ol class="address-monitoring-page__check-steps">
          <li :class="{ active: checkStage === 'beginning', done: ['tool88','saving','done'].includes(checkStage) }">Prepare payload</li>
          <li :class="{ active: checkStage === 'tool88', done: ['saving','done'].includes(checkStage) }">Run Tool 88</li>
          <li :class="{ active: checkStage === 'saving', done: checkStage === 'done' }">Save result</li>
        </ol>
        <p v-if="progressText(tool88Progress)" class="address-monitoring-page__progress-text">{{ progressText(tool88Progress) }}</p>
        <p class="address-monitoring-page__run-lock-note">Check buttons stay locked while this run is active. You can minimize this window to review items and history.</p>
      </div>
    </div>

    <aside v-if="isRunActive && runVisualMode === 'dock'" class="address-monitoring-page__runner-dock" role="status" aria-live="polite" aria-label="Tool 88 monitoring run in progress">
      <div class="address-monitoring-page__runner-dock-top">
        <div class="address-monitoring-page__runner-dock-pulse" aria-hidden="true"><span></span></div>
        <div>
          <p class="address-monitoring-page__eyebrow">{{ checkOverlayEyebrow }}</p>
          <h2>{{ dockTitle }}</h2>
        </div>
      </div>
      <p class="address-monitoring-page__runner-dock-item">{{ checkingItemLabel }}</p>
      <p v-if="batchRun.active" class="address-monitoring-page__runner-dock-status">{{ batchInlineStatus }}</p>
      <p v-else class="address-monitoring-page__runner-dock-status">{{ checkOverlayMessage }}</p>
      <div class="address-monitoring-page__runner-dock-progress" aria-hidden="true">
        <span :style="{ width: runVisualProgressPercent + '%' }"></span>
      </div>
      <p class="address-monitoring-page__runner-dock-lock">Check controls are locked; review/history controls remain available.</p>
      <div class="address-monitoring-page__runner-dock-actions">
        <button type="button" class="primary" @click="expandRunVisual">Make big again</button>
        <button v-if="unacknowledgedMovementItems.length" type="button" @click="reviewFirstMovementAlert">Review alert</button>
      </div>
    </aside>

    <!-- Hidden Tool 88 host. Keep this mounted so the monitor can reuse the Tool 88 engine. -->
    <div class="address-monitoring-page__tool88-host" aria-hidden="true">
      <Tool88Page_CaseBuddy
        ref="tool88Monitor"
        :case-buddy-embedded="true"
        :case-buddy-start-in-advanced-view="false"
        @case-buddy-progress="handleTool88Progress"
        @case-buddy-result="handleTool88Result"
        @case-buddy-guardrail="handleTool88Guardrail"
      />
    </div>
  </div>
</template>

<script>
import Tool88Page_CaseBuddy from './Tool88Page_CaseBuddy.vue';
import { authenticatedFetch } from '@/services/authenticatedFetch';

const API_BASE = '/api/casebuddy/monitoring';
//const CHECK_COOLDOWN_MS = 6 * 60 * 60 * 1000;
const CASE_ROW_WIDTH = 24; // A-X
const CASE_BUDDY_MIN_TSV_COLUMNS = 18; // A-R is enough for Tool 61-style quick paste; A-X gives richer Case Buddy context.
const BTC_SATOSHI_DIVISOR = 100000000;
const COL = Object.freeze({
  PATH_LABEL: 0,
  TX_HASH: 1,
  FROM_ADDRESS: 2,
  TO_ADDRESS: 3,
  DATE: 4,
  AMOUNT: 5,
  TOKEN: 6,
  SYSTEM_NOTES: 9,
  TO_ATTRIBUTION: 10,
  ANALYST_NOTES: 11,
  FUNDS_FOLLOWED: 12,
  PRIOR_BALANCE: 13,
  FROM_HASH: 14,
  CONTINUES_AS: 16,
  LEFT_BOUNDS_DISPLAY: 17,
  LEFT_BOUNDS_NUMERIC: 18,
  CHAIN_ID: 19,
  CHAIN_NAME: 20,
  HOP_LOGIC: 21,
  TOOL88_NOTES: 22,
  FROM_ATTRIBUTION: 23,
});

function emptyManualAddForm() {
  return {
    label: '',
    case_name: '',
    source_path_label: '1',
    transaction_hash: '',
    from_address: '',
    monitor_address: '',
    transaction_utc: '',
    transaction_amount: '',
    monitor_funds_amount: '',
    token_symbol: '',
    left_bounds_amount: '0',
    prior_balance_amount: '',
    chain_id: '',
    chain_name: '',
    hop_logic: 'FIFO',
    from_hash: '',
    user_note: '',
  };
}

function blankSession() {
  return {
    isAuthenticated: false,
    username: '',
    role: null,
    canUseAllTools: false,
    canManualAdd: false,
    source: 'none',
  };
}

export default {
  name: 'AddressMonitoringPage',
  components: { Tool88Page_CaseBuddy },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    caseFilter: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'items-loaded'],
  data() {
    return {
      items: [],
      selectedItemId: null,
      searchQuery: '',
      statusFilter: 'all',
      loading: false,
      checkingAny: false,
      feedback: '',
      feedbackTone: 'info',
      currentCheck: null,
      tool88Progress: null,
      acknowledgementSaving: false,
      session: blankSession(),
      sessionLoading: false,
      showManualAdd: false,
      manualAddSaving: false,
      manualAdd: emptyManualAddForm(),
      manualTsv: {
        rowText: '',
        message: '',
        tone: 'info',
        lastParsed: null,
      },
      caseFilterEnabled: true,
      sortMode: 'due_asc',
      showFilters: false,
      utilityOpen: false,
      utilityTab: 'help',
      guideOpen: false,
      guideTab: 'overview',
      lastLoadedAt: '',
      historyOpen: false,
      historyLoading: false,
      historyItems: [],
      historyItemId: null,
      checkStage: 'idle',
      checkingItem: null,
      runVisualMode: 'modal',
      batchRun: {
        active: false,
        total: 0,
        currentIndex: 0,
        completed: 0,
        failed: 0,
        skipped: 0,
        phase: 'idle',
        currentItemLabel: '',
        nextItemLabel: '',
        startedAt: '',
        finishedAt: '',
      },
      lastRunResult: null,
      notificationLoading: false,
      notificationSaving: false,
      notificationSettings: {
        configured: false,
        enabled: false,
        masked_target: '',
        updated_at_utc: '',
      },
      notificationFormUrl: '',
      notificationFeedback: '',
      notificationTone: 'info',
    };
  },
  computed: {
    caseFilteredItems() {
      if (!this.embedded || !this.caseFilterEnabled) return this.items;
      return this.items.filter((item) => this.itemMatchesCaseFilter(item));
    },
    selectedItem() {
      return this.items.find((item) => item.id === this.selectedItemId) || null;
    },
    dueItems() {
      return this.caseFilteredItems.filter((item) => this.isItemDue(item));
    },
    movementItems() {
      return this.caseFilteredItems.filter((item) => this.isMovementStatus(item));
    },
    unacknowledgedMovementItems() {
      return this.movementItems
        .filter((item) => this.isUnacknowledgedMovement(item))
        .sort((a, b) => this.movementAlertTimeMs(b) - this.movementAlertTimeMs(a));
    },
    acknowledgedMovementItems() {
      return this.movementItems.filter((item) => this.isAcknowledgedMovement(item));
    },
    movementTotal() {
      return this.statusCounts.movement_found + this.statusCounts.partial_movement_found;
    },
    reviewAttentionTotal() {
      return this.statusCounts.needs_review + this.statusCounts.error;
    },
    statusCounts() {
      const counts = {
        dormant: 0,
        movement_found: 0,
        partial_movement_found: 0,
        needs_review: 0,
        error: 0,
        disabled: 0,
        deleted: 0,
      };
      this.caseFilteredItems.forEach((item) => {
        if (Object.prototype.hasOwnProperty.call(counts, item.status)) counts[item.status] += 1;
      });
      return counts;
    },
    summaryCards() {
      return [
        {
          key: 'total',
          filter: 'all',
          label: this.embedded && this.caseFilterEnabled ? 'In current case' : 'Total',
          value: this.caseFilteredItems.length,
          hint: this.embedded && this.caseFilterEnabled ? `${this.items.length} cross-case total` : 'All active monitor items',
        },
        {
          key: 'due',
          filter: 'due',
          label: 'Due now',
          value: this.dueItems.length,
          hint: 'Ready for a Tool 88 check',
        },
        {
          key: 'dormant',
          filter: 'dormant',
          label: 'Dormant',
          value: this.statusCounts.dormant,
          hint: 'No known movement yet',
        },
        {
          key: 'movement',
          filter: 'movement_any',
          label: 'Movement found',
          value: this.movementTotal,
          hint: 'Review before report use',
        },
        {
          key: 'attention',
          filter: 'review_attention',
          label: 'Needs review',
          value: this.reviewAttentionTotal,
          hint: 'Manual review or error follow-up',
        },
      ];
    },
    filteredItems() {
      const query = this.searchQuery.toLowerCase();
      const base = this.caseFilteredItems.filter((item) => {
        if (!this.itemMatchesStatusFilter(item)) return false;
        if (!query) return true;
        return [
          item.label,
          item.case_name,
          item.case_file_id,
          item.case_display_id,
          item.transaction_hash,
          item.monitor_address,
          item.token_symbol,
          item.chain_name,
          item.source_path_label,
          item.created_from,
        ].some((value) => String(value || '').toLowerCase().includes(query));
      });
      return this.sortItems(base);
    },
    activeFilterLabel() {
      const parts = [];
      if (this.statusFilter !== 'all') parts.push(this.formatStatusFilter(this.statusFilter));
      if (this.searchQuery) parts.push(`search: ${this.searchQuery}`);
      if (this.embedded && this.caseFilterEnabled) parts.push('current case scope');
      return parts.join(' · ');
    },
    emptyStateTitle() {
      if (!this.items.length) return 'No monitoring items yet';
      if (this.embedded && this.caseFilterEnabled && !this.caseFilteredItems.length) return 'No items for this case yet';
      return 'No items match this view';
    },
    emptyStateMessage() {
      if (!this.items.length) {
        return this.canManualAdd
          ? 'Add an item manually, import from Tool 61, or add eligible dormant rows from Case Buddy.'
          : 'Add eligible dormant/path-ending rows from Case Buddy to start monitoring.';
      }
      if (this.embedded && this.caseFilterEnabled && !this.caseFilteredItems.length) {
        return 'This monitor has items, but none match the active Case Buddy file or case label. Turn off the current-case filter to see cross-case items.';
      }
      return 'Try clearing the search/status filters or changing the sort order.';
    },
    caseFilterLabel() {
      if (!this.embedded) return '';
      const name = String(this.caseFilter?.caseName || this.caseFilter?.case_name || '').trim();
      const file = String(this.caseFilter?.caseFileName || this.caseFilter?.case_file_name || '').trim();
      const label = name || file || 'current case';
      return `Current case only: ${label}`;
    },
    recommendation() {
      if (!this.session?.isAuthenticated) {
        return {
          action: 'read_guide',
          title: 'Sign in before checking monitors',
          body: 'The monitoring list is scoped to the authenticated user. Refresh the session after logging in.',
        };
      }
      if (this.unacknowledgedMovementItems.length) {
        return {
          action: 'review_movement_alerts',
          title: `${this.unacknowledgedMovementItems.length} movement alert${this.unacknowledgedMovementItems.length === 1 ? '' : 's'} need acknowledgement`,
          body: 'Movement findings float to the top until you review and acknowledge them. Acknowledgement does not delete the record.',
        };
      }
      if (this.dueItems.length) {
        return {
          action: 'check_due',
          title: `${this.dueItems.length} item${this.dueItems.length === 1 ? '' : 's'} ready to check`,
          body: 'Run Tool 88 on due items when you are ready. Checks are manual and respect cooldown state.',
        };
      }
      if (this.reviewAttentionTotal) {
        return {
          action: 'review_attention',
          title: `${this.reviewAttentionTotal} item${this.reviewAttentionTotal === 1 ? '' : 's'} need attention`,
          body: 'Review errors or needs-review results before relying on the monitoring status.',
        };
      }
      if (this.searchQuery || this.statusFilter !== 'all') {
        return {
          action: 'clear_filters',
          title: 'Filtered monitoring view',
          body: 'You are viewing a narrowed subset. Clear filters to review the full monitoring queue.',
        };
      }
      return {
        action: 'read_guide',
        title: 'Monitoring queue is current',
        body: 'No scoped items are due right now. You can inspect history, add items from Case Buddy, or review how the monitor works.',
      };
    },
    guideTabs() {
      return [
        { key: 'overview', label: 'Overview' },
        { key: 'workflow', label: 'Workflow' },
        { key: 'statuses', label: 'Statuses' },
        { key: 'casebuddy', label: this.embedded ? 'Embedded view' : 'Case Buddy' },
      ];
    },
    statusGuide() {
      return [
        { key: 'dormant', label: 'Dormant', description: 'The item is active and no movement has been found by the latest successful check.' },
        { key: 'movement_found', label: 'Movement found', description: 'Tool 88 found a likely outgoing movement from the monitored hash/address. The alert stays outstanding until acknowledged.' },
        { key: 'partial_movement_found', label: 'Partial movement', description: 'Some funds appear to have moved while a remainder may still be dormant. The alert stays outstanding until acknowledged.' },
        { key: 'needs_review', label: 'Needs review', description: 'Tool 88 could not safely classify the item without analyst review.' },
        { key: 'error', label: 'Error', description: 'A provider, auth, malformed item, or Tool 88 issue prevented a clean check.' },
        { key: 'disabled', label: 'Stopped', description: 'The item is kept for records but skipped by due checks.' },
      ];
    },
    selectedStatusExplanation() {
      const item = this.selectedItem || {};
      const status = item.status || '';
      const acknowledged = this.isAcknowledgedMovement(item);
      const map = {
        dormant: {
          title: 'No movement currently recorded',
          body: 'The item remains active. Run a check when it is due to ask Tool 88 whether a later hop exists.',
        },
        movement_found: {
          title: acknowledged ? 'Movement found · acknowledged' : 'Movement found · needs acknowledgement',
          body: acknowledged
            ? 'This movement finding has been reviewed and dismissed from the outstanding alert queue. The result remains stored for history.'
            : 'Review the movement hash/address and decide how it should affect the case, addendum, or follow-up workflow. Then acknowledge the alert.',
        },
        partial_movement_found: {
          title: acknowledged ? 'Partial movement · acknowledged' : 'Partial movement · needs acknowledgement',
          body: acknowledged
            ? 'This partial movement finding has been reviewed and dismissed from the outstanding alert queue. The result remains stored for history.'
            : 'Some movement was detected. Review the remaining dormant amount before creating any replacement monitor item, then acknowledge the alert.',
        },
        needs_review: {
          title: 'Analyst review needed',
          body: 'Tool 88 found ambiguity, guardrails, bridge/swap behavior, or output it could not classify automatically.',
        },
        error: {
          title: 'Last check failed',
          body: 'The item can usually be retried after the shorter error cooldown, but the error text should be reviewed first.',
        },
        disabled: {
          title: 'Monitoring stopped',
          body: 'Stopped items are retained for context but skipped when checking all due items.',
        },
      };
      return map[status] || {
        title: 'Monitor item selected',
        body: 'Review the saved basis before running a Tool 88 monitoring check.',
      };
    },
    notificationLabel() {
      if (this.notificationLoading) return 'Checking notifications…';
      if (this.notificationSettings?.configured) return 'Discord alerts on';
      return 'Discord alerts off';
    },
    selectedLastResultTone() {
      const status = this.selectedItem?.status || '';
      if (this.isAcknowledgedMovement(this.selectedItem)) return 'acknowledged';
      if (['movement_found', 'partial_movement_found'].includes(status)) return 'movement';
      if (['needs_review', 'error'].includes(status)) return 'attention';
      if (status === 'dormant') return 'quiet';
      return 'neutral';
    },
    lastRunTone() {
      const status = this.lastRunResult?.check_status || this.lastRunResult?.status || '';
      if (['movement_found', 'partial_movement_found'].includes(status)) return 'movement';
      if (['needs_review', 'error'].includes(status)) return 'attention';
      if (status === 'no_movement') return 'quiet';
      return 'neutral';
    },
    isRunActive() {
      return !!(this.checkingAny || this.batchRun.active);
    },
    batchProgressPercent() {
      if (!this.batchRun.total) return 0;
      const finished = this.batchRun.completed + this.batchRun.failed + this.batchRun.skipped;
      return Math.max(4, Math.min(100, Math.round((finished / this.batchRun.total) * 100)));
    },
    runVisualProgressPercent() {
      if (this.batchRun.active) return this.batchProgressPercent;
      const stagePercent = {
        queueing: 8,
        queueing_next: 12,
        beginning: 18,
        tool88: 55,
        saving: 84,
        done: 100,
        error: 100,
      };
      return stagePercent[this.checkStage] || (this.isRunActive ? 8 : 0);
    },
    batchStatusTitle() {
      if (this.batchRun.phase === 'finished') return 'Due-check queue complete';
      if (this.batchRun.phase === 'queueing_next') return 'Preparing the next monitoring check';
      if (this.batchRun.phase === 'finishing') return 'Finishing the due-check queue';
      if (this.batchRun.phase === 'preparing') return `Starting item ${this.batchRun.currentIndex} of ${this.batchRun.total}`;
      return `Checking due items · ${this.batchRun.completed + this.batchRun.failed + this.batchRun.skipped} of ${this.batchRun.total} complete`;
    },
    batchStatusMessage() {
      const bits = [];
      if (this.batchRun.currentItemLabel) bits.push(`Current: ${this.batchRun.currentItemLabel}`);
      if (this.batchRun.phase === 'queueing_next' && this.batchRun.nextItemLabel) bits.push(`Next: ${this.batchRun.nextItemLabel}`);
      if (this.batchRun.failed) bits.push(`${this.batchRun.failed} had errors and will remain available for review.`);
      if (!bits.length) return 'The monitor is keeping the queue active between Tool 88 runs. Keep this page open.';
      return bits.join(' · ');
    },
    batchInlineStatus() {
      if (!this.batchRun.active) return '';
      const finished = this.batchRun.completed + this.batchRun.failed + this.batchRun.skipped;
      if (this.batchRun.phase === 'queueing_next') {
        return `Queue progress: ${finished} of ${this.batchRun.total} complete. Preparing the next item now.`;
      }
      return `Queue progress: ${finished} of ${this.batchRun.total} complete.`;
    },
    checkingItemLabel() {
      const item = this.checkingItem || this.selectedItem || {};
      if (item.label || item.case_name || item.transaction_hash) return item.label || item.case_name || item.transaction_hash;
      return this.batchRun.currentItemLabel || this.batchRun.nextItemLabel || 'selected monitoring item';
    },
    checkOverlayEyebrow() {
      return this.batchRun.active ? 'Queued Tool 88 checks' : 'Tool 88 check running';
    },
    checkOverlayTitle() {
      const labels = {
        queueing: 'Preparing the queued monitoring check',
        queueing_next: 'Preparing the next queued check',
        beginning: 'Starting the monitoring check',
        tool88: 'Tool 88 is checking for movement',
        saving: 'Saving the check result',
        done: this.batchRun.active ? 'Queue step complete' : 'Check complete',
        error: 'Check failed',
      };
      return labels[this.checkStage] || 'Running monitoring check';
    },
    dockTitle() {
      if (this.batchRun.active) {
        const finished = this.batchRun.completed + this.batchRun.failed + this.batchRun.skipped;
        return `${finished}/${this.batchRun.total} checked`;
      }
      return this.checkOverlayTitle;
    },
    checkOverlayMessage() {
      if (this.checkStage === 'queueing') return 'The due-check queue is active. The next item is being prepared for Tool 88.';
      if (this.checkStage === 'queueing_next') return 'The previous item finished. The monitor is keeping the queue alive and preparing the next Tool 88 check.';
      if (this.checkStage === 'beginning') return 'Opening a server-side check record and preparing the one-item Tool 88 payload.';
      if (this.checkStage === 'tool88') return 'Keep this page open. Tool 88 is looking for a one-hop movement from the monitored hash or address.';
      if (this.checkStage === 'saving') return 'The Tool 88 result is being stored and the monitor item is being updated.';
      return 'The monitor is working on the selected item.';
    },
    manualPreviewRow() {
      try {
        return this.buildManualSourceRow();
      } catch {
        return [];
      }
    },
    sessionRole() {
      return String(this.session?.role || '').toLowerCase();
    },
    canManualAdd() {
      return !!(
        this.session?.canManualAdd
        || this.session?.canUseAllTools
        || ['admin', 'mod', 'moderator'].includes(this.sessionRole)
      );
    },
    sessionLabel() {
      if (this.sessionLoading) return 'Checking login…';
      if (!this.session?.isAuthenticated) return 'Not signed in';
      const who = this.session.username || 'signed-in user';
      const role = this.session.role ? ` · ${this.session.role}` : '';
      return `${who}${role}`;
    },
  },
  mounted() {
    this.initializePage();
  },
  methods: {
    async initializePage() {
      await this.refreshSession(false);
      await Promise.all([
        this.loadItems(),
        this.loadNotificationSettings(false),
      ]);
    },
    setFeedback(message, tone = 'info') {
      this.feedback = message;
      this.feedbackTone = tone;
    },
    clearCheckRunUiBeforeStart() {
      // A newly-started Tool 88 run makes the previous green/red check message stale.
      // Clear both the structured result banner and the generic feedback strip before any async work begins.
      this.feedback = '';
      this.feedbackTone = 'info';
      this.lastRunResult = null;
      this.currentCheck = null;
      this.tool88Progress = null;
    },
    minimizeRunVisual() {
      if (!this.isRunActive) return;
      this.runVisualMode = 'dock';
    },
    expandRunVisual() {
      if (!this.isRunActive) return;
      this.runVisualMode = 'modal';
    },
    resetRunVisualMode() {
      this.runVisualMode = 'modal';
    },
    isItemActivelyChecking(item) {
      if (!this.isRunActive || !item || !this.checkingItem) return false;
      const itemId = String(item.id || '');
      const checkingId = String(this.checkingItem.id || '');
      return Boolean(itemId && checkingId && itemId === checkingId);
    },
    resetBatchRun() {
      this.batchRun = {
        active: false,
        total: 0,
        currentIndex: 0,
        completed: 0,
        failed: 0,
        skipped: 0,
        phase: 'idle',
        currentItemLabel: '',
        nextItemLabel: '',
        startedAt: '',
        finishedAt: '',
      };
    },
    itemQueueLabel(item) {
      if (!item) return '';
      const base = item.label || item.case_name || this.shortHash(item.transaction_hash) || `Item ${item.id || ''}`.trim();
      const amount = this.formatAmount(item.monitor_funds_amount, item.token_symbol);
      return amount ? `${base} · ${amount}` : base;
    },
    sleep(ms = 0) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    },
    toggleGuide(tab = 'overview') {
      this.openUtility('help', tab);
    },
    openUtility(tab = 'help', guideTab = '') {
      this.utilityTab = tab || 'help';
      if (guideTab) this.guideTab = guideTab;
      this.utilityOpen = true;
      this.guideOpen = this.utilityTab === 'help';
      if (this.utilityTab === 'notifications') this.loadNotificationSettings(false).catch(() => {});
    },
    closeUtility() {
      this.utilityOpen = false;
      this.guideOpen = false;
    },
    setStatusFilter(filter = 'all') {
      this.statusFilter = filter;
    },
    clearFilters() {
      this.searchQuery = '';
      this.statusFilter = 'all';
      this.sortMode = 'due_asc';
    },
    itemMatchesStatusFilter(item) {
      if (this.statusFilter === 'all') return true;
      if (this.statusFilter === 'due') return this.isItemDue(item);
      if (this.statusFilter === 'movement_unacknowledged') return this.isUnacknowledgedMovement(item);
      if (this.statusFilter === 'movement_acknowledged') return this.isAcknowledgedMovement(item);
      if (this.statusFilter === 'movement_any') return this.isMovementStatus(item);
      if (this.statusFilter === 'review_attention') return ['needs_review', 'error'].includes(item.status);
      return item.status === this.statusFilter;
    },
    formatStatusFilter(filter) {
      const labels = {
        all: 'All statuses',
        due: 'Due now',
        dormant: 'Dormant',
        movement_unacknowledged: 'Outstanding movement alerts',
        movement_acknowledged: 'Acknowledged movement',
        movement_any: 'Any movement found',
        movement_found: 'Movement found',
        partial_movement_found: 'Partial movement',
        review_attention: 'Needs review or error',
        needs_review: 'Needs review',
        error: 'Error',
        disabled: 'Stopped',
      };
      return labels[filter] || this.formatStatus(filter);
    },
    sortItems(items) {
      const copy = [...items];
      const numericAmount = (item) => Number(String(item.monitor_funds_amount ?? '').replace(/,/g, '')) || 0;
      const dueTime = (item) => {
        if (!this.isItemDue(item)) {
          const nextValue = item.next_check_after_utc || item.next_check_after;
          const parsed = nextValue ? this.parseUtcDateMs(nextValue) : Number.POSITIVE_INFINITY;
          return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
        }
        return 0;
      };
      const dateValue = (item, keys) => {
        for (const key of keys) {
          const parsed = this.parseUtcDateMs(item?.[key]);
          if (Number.isFinite(parsed)) return parsed;
        }
        return 0;
      };
      const alertRank = (item) => (this.isUnacknowledgedMovement(item) ? 0 : 1);
      const statusRank = (item) => ({
        movement_found: this.isUnacknowledgedMovement(item) ? 0 : 4,
        partial_movement_found: this.isUnacknowledgedMovement(item) ? 1 : 5,
        needs_review: 2,
        error: 3,
        dormant: 6,
        disabled: 7,
        deleted: 8,
      }[item.status] ?? 9);
      copy.sort((a, b) => {
        const alertDifference = alertRank(a) - alertRank(b);
        if (alertDifference) return alertDifference;
        if (this.isUnacknowledgedMovement(a) && this.isUnacknowledgedMovement(b)) {
          const movementDifference = this.movementAlertTimeMs(b) - this.movementAlertTimeMs(a);
          if (movementDifference) return movementDifference;
        }
        if (this.sortMode === 'last_checked_desc') {
          return dateValue(b, ['last_checked_at_utc', 'last_checked_at']) - dateValue(a, ['last_checked_at_utc', 'last_checked_at']);
        }
        if (this.sortMode === 'created_desc') {
          return dateValue(b, ['created_at_utc', 'created_at']) - dateValue(a, ['created_at_utc', 'created_at']);
        }
        if (this.sortMode === 'case_asc') {
          return String(a.case_name || a.label || '').localeCompare(String(b.case_name || b.label || ''));
        }
        if (this.sortMode === 'amount_desc') {
          return numericAmount(b) - numericAmount(a);
        }
        if (this.sortMode === 'status_asc') {
          return statusRank(a) - statusRank(b) || dueTime(a) - dueTime(b);
        }
        return dueTime(a) - dueTime(b) || statusRank(a) - statusRank(b);
      });
      return copy;
    },
    getStoredUsername() {
      try {
        return localStorage.getItem('username') || '';
      } catch {
        return '';
      }
    },
    monitoringApiErrorMessage(json, fallback = 'Request failed') {
      if (!json || typeof json !== 'object') return fallback;
      const base = json.message || fallback;
      const requestId = json.request_id || json.requestId || '';
      return requestId ? `${base} (Request ${requestId})` : base;
    },
    normalizeSession(raw = {}, source = 'unknown') {
      const role = raw.role ?? raw.user_role ?? null;
      const username = raw.username || this.getStoredUsername();
      const canUseAllTools = !!(raw.canUseAllTools ?? raw.can_use_all_tools ?? false);
      const roleText = String(role || '').toLowerCase();
      const canManualAdd = !!(
        raw.canManualAdd
        || raw.can_manual_add
        || canUseAllTools
        || ['admin', 'mod', 'moderator'].includes(roleText)
      );
      return {
        isAuthenticated: !!(raw.isAuthenticated ?? raw.is_authenticated ?? true),
        username,
        role,
        canUseAllTools,
        canManualAdd,
        source,
      };
    },
    applySession(raw, source = 'unknown') {
      this.session = this.normalizeSession(raw, source);
    },
    async parseMonitoringResponse(response, fallback) {
      const json = await response.json().catch(() => null);
      if (!response.ok || !json || json.status === 'error') {
        throw new Error(this.monitoringApiErrorMessage(json, fallback));
      }
      return json;
    },
    async apiGet(path, query = {}) {
      const params = new URLSearchParams();
      Object.entries(query || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const response = await authenticatedFetch(`${API_BASE}/${path}${suffix}`);
      return this.parseMonitoringResponse(response, `Request failed: ${path}`);
    },
    async apiPost(path, body = {}) {
      const response = await authenticatedFetch(`${API_BASE}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return this.parseMonitoringResponse(response, `Request failed: ${path}`);
    },
    async loadMonitoringSession() {
      const json = await this.apiGet('session.php');
      return this.normalizeSession(json.user || {}, 'monitoring');
    },
    async refreshSession(showFeedback = true) {
      this.sessionLoading = true;
      try {
        this.session = await this.loadMonitoringSession();
        if (showFeedback) {
          this.setFeedback(
            this.canManualAdd
              ? 'Session recognized. Manual add is enabled for this account.'
              : 'Session refreshed. Manual add is only available for staff or All Tools accounts.',
            this.canManualAdd ? 'success' : 'info'
          );
        }
      } catch (error) {
        this.session = blankSession();
        if (showFeedback) this.setFeedback(error?.message || 'Could not verify the current session.', 'error');
      } finally {
        this.sessionLoading = false;
      }
    },
    async loadItems() {
      this.loading = true;
      try {
        const json = await this.apiGet('list_items.php');
        if (json.meta?.user || json.user) {
          this.applySession(json.meta?.user || json.user, 'monitoring');
        }
        this.items = Array.isArray(json.items) ? json.items : [];
        this.lastLoadedAt = new Date().toISOString();
        if (this.selectedItemId && !this.items.some((item) => item.id === this.selectedItemId)) {
          this.selectedItemId = null;
        }
        this.$emit('items-loaded', {
          count: this.items.length,
          filteredCount: this.caseFilteredItems.length,
          visibleCount: this.filteredItems.length,
          caseFilterEnabled: this.caseFilterEnabled
        });
      } catch (error) {
        this.setFeedback(error.message || 'Could not load monitoring items.', 'error');
      } finally {
        this.loading = false;
      }
    },
    async loadNotificationSettings(showFeedback = false) {
      this.notificationLoading = true;
      try {
        const json = await this.apiGet('get_notification_settings.php');
        this.notificationSettings = {
          configured: !!json.discord?.configured,
          enabled: !!json.discord?.enabled,
          masked_target: json.discord?.masked_target || '',
          updated_at_utc: json.discord?.updated_at_utc || '',
        };
        this.notificationFormUrl = '';
        this.notificationFeedback = showFeedback ? 'Notification settings refreshed.' : this.notificationFeedback;
        this.notificationTone = 'success';
      } catch (error) {
        if (showFeedback) {
          this.notificationFeedback = error?.message || 'Could not load notification settings.';
          this.notificationTone = 'error';
        }
      } finally {
        this.notificationLoading = false;
      }
    },
    validateDiscordWebhookUrl(value) {
      const text = String(value || '').trim();
      if (!text) return 'Paste a Discord webhook URL first.';
      try {
        const url = new URL(text);
        const allowedHosts = ['discord.com', 'discordapp.com', 'ptb.discord.com', 'canary.discord.com'];
        if (url.protocol !== 'https:' || !allowedHosts.includes(url.hostname)) return 'Use a Discord webhook URL from discord.com.';
        if (url.username || url.password || url.port || url.search || url.hash) return 'Use the plain Discord webhook URL without extra parameters.';
        if (!/^\/api\/webhooks\/\d+\/[A-Za-z0-9_.-]+\/?$/.test(url.pathname)) return 'That does not look like a Discord webhook URL.';
      } catch {
        return 'That does not look like a valid URL.';
      }
      return '';
    },
    async saveDiscordWebhook() {
      const validation = this.validateDiscordWebhookUrl(this.notificationFormUrl);
      if (validation) {
        this.notificationFeedback = validation;
        this.notificationTone = 'error';
        return;
      }
      this.notificationSaving = true;
      try {
        const json = await this.apiPost('save_notification_settings.php', {
          discord_webhook_url: this.notificationFormUrl.trim(),
        });
        this.notificationSettings = {
          configured: !!json.discord?.configured,
          enabled: !!json.discord?.enabled,
          masked_target: json.discord?.masked_target || '',
          updated_at_utc: json.discord?.updated_at_utc || '',
        };
        this.notificationFormUrl = '';
        this.notificationFeedback = json.message || 'Discord movement alerts saved.';
        this.notificationTone = 'success';
      } catch (error) {
        this.notificationFeedback = error?.message || 'Discord webhook could not be saved.';
        this.notificationTone = 'error';
      } finally {
        this.notificationSaving = false;
      }
    },
    async removeDiscordWebhook() {
      if (!this.notificationSettings?.configured) return;
      if (!confirm('Remove the saved Discord webhook for movement alerts?')) return;
      this.notificationSaving = true;
      try {
        const json = await this.apiPost('delete_notification_settings.php', {
          target_type: 'discord_webhook',
        });
        this.notificationSettings = {
          configured: false,
          enabled: false,
          masked_target: '',
          updated_at_utc: '',
        };
        this.notificationFormUrl = '';
        this.notificationFeedback = json.message || 'Discord webhook removed.';
        this.notificationTone = 'success';
      } catch (error) {
        this.notificationFeedback = error?.message || 'Discord webhook could not be removed.';
        this.notificationTone = 'error';
      } finally {
        this.notificationSaving = false;
      }
    },
    resetManualAddForm() {
      this.manualAdd = emptyManualAddForm();
      this.clearManualTsvPaste(true);
    },
    cleanDecimal(value) {
      const text = String(value ?? '').replace(/,/g, '').trim();
      if (!text) return '';
      const match = text.match(/^-?(?:\d+(?:\.\d+)?|\.\d+)/);
      if (!match) return '';
      let out = match[0];
      if (out.startsWith('.')) out = `0${out}`;
      if (out.startsWith('-.')) out = `-0${out.slice(1)}`;
      return out;
    },
    normalizeTsvCell(value) {
      return String(value ?? '').replace(/\u00A0/g, ' ').trim();
    },
    normalizeManualTsvText(value) {
      return String(value ?? '')
        .replace(/\u00A0/g, ' ')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n+$/, '');
    },
    parseAmountTokenCell(value) {
      const text = this.normalizeTsvCell(value);
      const amount = this.cleanDecimal(text);
      const amountMatch = text.match(/-?(?:\d[\d,]*(?:\.\d+)?|\.\d+)/);
      const afterAmount = amountMatch ? text.slice(text.indexOf(amountMatch[0]) + amountMatch[0].length) : '';
      const tokenMatch = afterAmount.match(/[A-Za-z][A-Za-z0-9._-]*/);
      return {
        amount,
        token: tokenMatch ? String(tokenMatch[0]).toUpperCase() : '',
      };
    },
    parseLeftBoundsFromTsv(displayCell, numericCell, tokenSymbol, transactionAmount) {
      const numeric = this.cleanDecimal(numericCell);
      if (numeric) return numeric;

      const firstDisplayLine = this.normalizeManualTsvText(displayCell).split('\n')[0] || '';
      const displayNumber = this.cleanDecimal(firstDisplayLine);
      if (!displayNumber) return '0';

      const rawNumber = Number(displayNumber);
      const txNumber = Number(this.cleanDecimal(transactionAmount));
      if (
        String(tokenSymbol || '').toUpperCase() === 'BTC' &&
        Number.isFinite(rawNumber) &&
        Number.isFinite(txNumber) &&
        Number.isInteger(rawNumber) &&
        rawNumber >= 1000 &&
        rawNumber > txNumber
      ) {
        return this.formatPlainDecimal(rawNumber / BTC_SATOSHI_DIVISOR, 8);
      }

      return displayNumber;
    },
    formatPlainDecimal(value, maxDigits = 18) {
      if (!Number.isFinite(value)) return '';
      return value
        .toFixed(maxDigits)
        .replace(/\.0+$/, '')
        .replace(/(\.\d*?)0+$/, '$1')
        .replace(/\.$/, '');
    },
    toDatetimeLocalValue(value) {
      const text = this.normalizeTsvCell(value);
      if (!text || text.startsWith('0000-00-00')) return '';
      const normalized = text.replace(' ', 'T');
      const match = normalized.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?/);
      if (!match) return '';
      return `${match[1]}T${match[2]}`;
    },
    inferManualChain(transactionHash, monitorAddress, tokenSymbol) {
      const tx = String(transactionHash || '').toLowerCase();
      const address = String(monitorAddress || '').toLowerCase();
      const token = String(tokenSymbol || '').toUpperCase();
      if (token === 'BTC' || /^(bc1|tb1|[13])[a-z0-9]/i.test(address)) {
        return { chain_id: 'bitcoin', chain_name: 'Bitcoin' };
      }
      if (tx.startsWith('0x') || address.startsWith('0x')) {
        return { chain_id: '1', chain_name: 'Ethereum' };
      }
      return { chain_id: '', chain_name: '' };
    },
    setManualTsvMessage(message, tone = 'info') {
      this.manualTsv.message = message;
      this.manualTsv.tone = tone;
    },
    clearManualTsvPaste(clearText = true) {
      if (clearText) this.manualTsv.rowText = '';
      this.manualTsv.message = '';
      this.manualTsv.tone = 'info';
      this.manualTsv.lastParsed = null;
    },
    parseManualCaseBuddyTsv(rawText) {
      const normalized = this.normalizeManualTsvText(rawText);
      if (!normalized.trim()) {
        return { ok: false, message: 'Paste one copied Case Buddy row first.' };
      }

      const columns = normalized.split('\t');
      if (columns.length < CASE_BUDDY_MIN_TSV_COLUMNS) {
        return {
          ok: false,
          message: `That paste produced ${columns.length} column${columns.length === 1 ? '' : 's'}. Paste one full Case Buddy row, ideally through column X.`,
        };
      }

      const get = (index) => this.normalizeTsvCell(columns[index] ?? '');
      const transactionHash = get(COL.TX_HASH);
      const fromAddress = get(COL.FROM_ADDRESS);
      const monitorAddress = get(COL.TO_ADDRESS);
      const transactionUtc = this.toDatetimeLocalValue(get(COL.DATE));
      const transactionAmount = this.cleanDecimal(get(COL.AMOUNT));
      const amountCell = this.parseAmountTokenCell(get(COL.AMOUNT));
      const fundsCell = this.parseAmountTokenCell(get(COL.FUNDS_FOLLOWED));
      const tokenSymbol = (get(COL.TOKEN) || fundsCell.token || amountCell.token).toUpperCase();
      const monitoredAmount = fundsCell.amount || amountCell.amount;
      const leftBounds = this.parseLeftBoundsFromTsv(
        get(COL.LEFT_BOUNDS_DISPLAY),
        get(COL.LEFT_BOUNDS_NUMERIC),
        tokenSymbol,
        transactionAmount || monitoredAmount
      );
      const chain = {
        chain_id: get(COL.CHAIN_ID),
        chain_name: get(COL.CHAIN_NAME),
      };
      const inferredChain = !chain.chain_id && !chain.chain_name
        ? this.inferManualChain(transactionHash, monitorAddress, tokenSymbol)
        : chain;

      if (!transactionHash) return { ok: false, message: 'Could not find a transaction hash in column B.' };
      if (!monitorAddress) return { ok: false, message: 'Could not find a monitored / To address in column D.' };
      if (!monitoredAmount) return { ok: false, message: 'Could not find funds followed in column M or amount in column F.' };
      if (!tokenSymbol) return { ok: false, message: 'Could not find a token in column G.' };

      const notes = [get(COL.ANALYST_NOTES), get(COL.SYSTEM_NOTES)].filter(Boolean).join('\n');
      const values = {
        label: this.manualAdd.label || '',
        case_name: this.manualAdd.case_name || '',
        source_path_label: get(COL.PATH_LABEL) || this.manualAdd.source_path_label || '1',
        transaction_hash: transactionHash,
        from_address: fromAddress,
        monitor_address: monitorAddress,
        transaction_utc: transactionUtc,
        transaction_amount: transactionAmount || monitoredAmount,
        monitor_funds_amount: monitoredAmount,
        token_symbol: tokenSymbol,
        left_bounds_amount: leftBounds || '0',
        prior_balance_amount: this.cleanDecimal(get(COL.PRIOR_BALANCE)),
        chain_id: inferredChain.chain_id,
        chain_name: inferredChain.chain_name,
        hop_logic: get(COL.HOP_LOGIC) || this.manualAdd.hop_logic || 'FIFO',
        from_hash: get(COL.FROM_HASH),
        user_note: this.manualAdd.user_note || notes,
      };

      return {
        ok: true,
        values,
        summary: {
          transaction_hash: transactionHash,
          monitor_address: monitorAddress,
          monitor_funds_amount: monitoredAmount,
          token_symbol: tokenSymbol,
          left_bounds_amount: leftBounds || '0',
        },
      };
    },
    applyManualTsvParse(rawText) {
      if (!this.canManualAdd) {
        this.setManualTsvMessage('Manual TSV paste is restricted to admin/mod/can-use-all-tools accounts.', 'error');
        return;
      }
      const parsed = this.parseManualCaseBuddyTsv(rawText);
      if (!parsed.ok) {
        this.setManualTsvMessage(parsed.message, 'error');
        return;
      }
      this.manualAdd = {
        ...this.manualAdd,
        ...parsed.values,
      };
      this.manualTsv.lastParsed = parsed.summary;
      this.setManualTsvMessage('Manual form filled from pasted Case Buddy row. Review it, then save the monitor item.', 'success');
    },
    handleManualTsvPaste(event) {
      const text = event?.clipboardData?.getData('text/plain') || '';
      this.manualTsv.rowText = text;
      this.applyManualTsvParse(text);
    },
    parseManualTsvFromTextarea() {
      this.applyManualTsvParse(this.manualTsv.rowText);
    },
    normalizeUtcInput(value) {
      const text = String(value || '').trim();
      if (!text) return '';
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) return `${text.replace('T', ' ')}:00`;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(text)) return text.replace('T', ' ');
      return text.replace('T', ' ');
    },
    validateManualAddForm() {
      if (!this.canManualAdd) return 'Manual add is restricted to admin/mod/can-use-all-tools accounts.';
      if (!this.manualAdd.transaction_hash) return 'Transaction hash is required.';
      if (!this.manualAdd.monitor_address) return 'Monitor / To address is required.';
      if (!this.manualAdd.token_symbol) return 'Token is required.';
      const amount = this.cleanDecimal(this.manualAdd.monitor_funds_amount);
      if (!amount || Number(amount) <= 0) return 'Monitor funds amount must be greater than 0.';
      const leftBounds = this.manualAdd.left_bounds_amount === '' ? '0' : this.cleanDecimal(this.manualAdd.left_bounds_amount);
      if (leftBounds === '') return 'Left bounds must be numeric or blank.';
      if (this.manualAdd.transaction_utc && !Number.isFinite(this.parseUtcDateMs(this.normalizeUtcInput(this.manualAdd.transaction_utc)))) {
        return 'Transaction UTC could not be parsed.';
      }
      return '';
    },
    buildManualSourceRow() {
      const f = this.manualAdd;
      const token = String(f.token_symbol || '').toUpperCase();
      const monitored = this.cleanDecimal(f.monitor_funds_amount);
      const leftBounds = f.left_bounds_amount === '' ? '0' : this.cleanDecimal(f.left_bounds_amount);
      const txUtc = this.normalizeUtcInput(f.transaction_utc);
      const row = Array.from({ length: CASE_ROW_WIDTH }, () => '');
      row[COL.PATH_LABEL] = f.source_path_label || '1';
      row[COL.TX_HASH] = f.transaction_hash || '';
      row[COL.FROM_ADDRESS] = f.from_address || '';
      row[COL.TO_ADDRESS] = f.monitor_address || '';
      row[COL.DATE] = txUtc;
      row[COL.AMOUNT] = this.cleanDecimal(f.transaction_amount) || monitored;
      row[COL.TOKEN] = token;
      row[COL.SYSTEM_NOTES] = 'Manual advanced address monitoring item.';
      row[COL.ANALYST_NOTES] = f.user_note || '';
      row[COL.FUNDS_FOLLOWED] = `${monitored} ${token}`.trim();
      row[COL.PRIOR_BALANCE] = this.cleanDecimal(f.prior_balance_amount);
      row[COL.FROM_HASH] = f.from_hash || '';
      row[COL.CONTINUES_AS] = 'Path ends.';
      row[COL.LEFT_BOUNDS_DISPLAY] = leftBounds || '0';
      row[COL.LEFT_BOUNDS_NUMERIC] = leftBounds || '0';
      row[COL.CHAIN_ID] = f.chain_id || '';
      row[COL.CHAIN_NAME] = f.chain_name || '';
      row[COL.HOP_LOGIC] = f.hop_logic || 'FIFO';
      row[COL.TOOL88_NOTES] = 'Manual advanced monitoring item.';
      return row;
    },
    buildManualAddPayload() {
      const f = this.manualAdd;
      const token = String(f.token_symbol || '').toUpperCase();
      const txUtc = this.normalizeUtcInput(f.transaction_utc);
      const monitored = this.cleanDecimal(f.monitor_funds_amount);
      const leftBounds = f.left_bounds_amount === '' ? '0' : this.cleanDecimal(f.left_bounds_amount);
      const sourceRow = this.buildManualSourceRow();
      return {
        created_from: 'manual_advanced',
        monitor_kind: 'manual_advanced',
        label: f.label,
        user_note: f.user_note,
        case_name: f.case_name,
        source_sheet: 'manual',
        source_path_label: f.source_path_label || '1',
        chain_id: f.chain_id,
        chain_name: f.chain_name,
        token_symbol: token,
        transaction_hash: f.transaction_hash,
        from_address: f.from_address,
        monitor_address: f.monitor_address,
        transaction_utc: txUtc,
        monitor_after_utc: txUtc,
        transaction_amount: this.cleanDecimal(f.transaction_amount) || monitored,
        source_funds_followed_amount: monitored,
        monitor_funds_amount: monitored,
        left_bounds_amount: leftBounds || '0',
        prior_balance_amount: this.cleanDecimal(f.prior_balance_amount),
        hop_logic: f.hop_logic || 'FIFO',
        system_notes: 'Manual advanced address monitoring item.',
        analyst_notes: f.user_note,
        tool88_notes: 'Manual advanced monitoring item.',
        from_hash: f.from_hash,
        continues_as_at_creation: 'Path ends.',
        source_row_json: sourceRow,
        tool88_input_json: {
          source: 'address_monitoring_manual_advanced',
          created_by: this.session.username || '',
          row: sourceRow,
        },
      };
    },
    async submitManualAdd() {
      const validationMessage = this.validateManualAddForm();
      if (validationMessage) {
        this.setFeedback(validationMessage, 'error');
        return;
      }
      this.manualAddSaving = true;
      try {
        const saved = await this.apiPost('create_item.php', this.buildManualAddPayload());
        this.setFeedback(saved.message || 'Manual monitoring item saved.', 'success');
        await this.loadItems();
        if (saved.item_id) this.selectedItemId = saved.item_id;
        this.resetManualAddForm();
        this.showManualAdd = false;
      } catch (error) {
        this.setFeedback(error?.message || 'Manual monitoring item could not be saved.', 'error');
      } finally {
        this.manualAddSaving = false;
      }
    },
    itemMatchesCaseFilter(item = {}) {
      const filter = this.caseFilter || {};
      const fileId = String(filter.caseFileId || filter.case_file_id || '').trim();
      const caseName = String(filter.caseName || filter.case_name || '').trim().toLowerCase();
      const fileName = String(filter.caseFileName || filter.case_file_name || '').trim().toLowerCase();
      if (!fileId && !caseName && !fileName) return true;
      const itemFileId = String(item.case_file_id || item.caseFileId || '').trim();
      if (fileId && itemFileId && itemFileId === fileId) return true;
      const itemCaseName = String(item.case_name || item.caseName || '').trim().toLowerCase();
      if (caseName && itemCaseName && itemCaseName === caseName) return true;
      if (fileName && itemCaseName && itemCaseName === fileName) return true;
      return false;
    },
    selectItem(item) {
      const nextId = item?.id || null;
      const changed = nextId !== this.selectedItemId;
      this.selectedItemId = nextId;
      if (changed) {
        this.historyItems = [];
        this.historyItemId = null;
        if (this.historyOpen && item) {
          this.loadHistory(item).catch(() => {});
        }
      }
    },
    isItemDue(item) {
      if (!item) return false;
      const dueStatuses = ['dormant', 'needs_review', 'error'];
      if (!dueStatuses.includes(item.status)) return false;
      const nextValue = item.next_check_after_utc || item.next_check_after;
      const next = nextValue ? this.parseUtcDateMs(nextValue) : 0;
      if (!Number.isFinite(next) || next <= 0) return true;
      return Date.now() >= next;
    },
    buildTool88RowsForMonitorItem(item) {
      const row = Array.from({ length: CASE_ROW_WIDTH }, () => '');
      row[COL.PATH_LABEL] = item.source_path_label || '1';
      row[COL.TX_HASH] = item.transaction_hash || '';
      row[COL.FROM_ADDRESS] = item.from_address || '';
      row[COL.TO_ADDRESS] = item.monitor_address || '';
      row[COL.DATE] = item.transaction_utc || item.monitor_after_utc || '';
      row[COL.AMOUNT] = item.transaction_amount ?? item.monitor_funds_amount ?? '';
      row[COL.TOKEN] = item.token_symbol || '';
      row[COL.SYSTEM_NOTES] = item.system_notes || 'Dormant funds monitoring check.';
      row[COL.FUNDS_FOLLOWED] = `${item.monitor_funds_amount} ${item.token_symbol}`;
      row[COL.PRIOR_BALANCE] = item.prior_balance_amount ?? '';
      row[COL.FROM_HASH] = item.from_hash || '';
      row[COL.CONTINUES_AS] = 'Path ends.';
      row[COL.LEFT_BOUNDS_DISPLAY] = item.left_bounds_amount ?? '0';
      row[COL.LEFT_BOUNDS_NUMERIC] = item.left_bounds_amount ?? '0';
      row[COL.CHAIN_ID] = item.chain_id || '';
      row[COL.CHAIN_NAME] = item.chain_name || '';
      row[COL.HOP_LOGIC] = item.hop_logic || 'FIFO';
      row[COL.TOOL88_NOTES] = item.tool88_notes || 'Case Buddy dormant monitoring check.';
      return [row];
    },
    buildTool88CandidateForMonitorItem(item) {
      return {
        rowIndex: 0,
        spreadsheetRowNumber: 1,
        pathLabel: item.source_path_label || '1',
        hash: item.transaction_hash,
        toAddress: item.monitor_address,
        targetAddress: item.monitor_address,
        fundsFollowed: `${item.monitor_funds_amount} ${item.token_symbol}`,
        leftBoundsDisplay: String(item.left_bounds_amount ?? '0'),
        leftBoundsNumeric: String(item.left_bounds_amount ?? '0'),
        chainId: item.chain_id || '',
        chainName: item.chain_name || '',
        hopLogic: item.hop_logic || 'FIFO',
      };
    },
    async checkItem(item, options = {}) {
      const isBatch = !!options.batch;
      if (!item || !this.isItemDue(item)) {
        return { ok: false, skipped: true, item_id: item?.id || null };
      }
      if (!isBatch) {
        this.resetBatchRun();
        this.resetRunVisualMode();
      }
      this.clearCheckRunUiBeforeStart();
      this.checkingAny = true;
      this.checkingItem = item;
      this.checkStage = 'beginning';
      let begin = null;
      let monitorItem = item;
      let outcome = { ok: false, item_id: item.id };
      try {
        begin = await this.apiPost('begin_check.php', { item_id: item.id });
        this.currentCheck = begin.check || null;
        const tool88Host = this.$refs.tool88Monitor;
        if (
          !begin.execution_context
          || !tool88Host
          || typeof tool88Host.setMonitoringExecutionContext !== 'function'
          || !tool88Host.setMonitoringExecutionContext(begin.execution_context)
        ) {
          throw new Error('The secure monitoring execution context could not be initialized.');
        }
        monitorItem = begin.item || item;
        this.checkingItem = monitorItem;
        if (!isBatch) this.selectedItemId = monitorItem.id || item.id;
        this.checkStage = 'tool88';
        const toolResult = await this.runTool88MonitorCheck(monitorItem, begin.tool88_monitor_payload || null);
        const interpreted = this.interpretTool88MonitorResult(monitorItem, toolResult);
        this.checkStage = 'saving';
        const executionContext = begin.execution_context || {};
        const monitoringAttestation = this.$refs.tool88Monitor?.getMonitoringAttestation?.() || null;
        const finished = await this.apiPost('finish_check.php', {
          item_id: monitorItem.id,
          check_id: begin.check_id,
          expected_state_version: executionContext.state_version,
          execution_token: executionContext.execution_token,
          monitoring_attestation: monitoringAttestation,
          ...interpreted,
        });
        this.lastRunResult = {
          ...interpreted,
          item_id: monitorItem.id,
          item: finished.item || monitorItem,
          notification_sent: !!finished.notification_sent,
          notification_error: finished.notification_error || '',
        };
        outcome = {
          ok: true,
          item_id: monitorItem.id,
          check_status: interpreted.check_status || interpreted.status || 'finished',
        };
        this.checkStage = 'done';
        // The result banner is now the single source of truth for completed check messaging.
        // Keeping the generic feedback strip empty prevents a stale green result from lingering when the next run starts.
        this.feedback = '';
        this.feedbackTone = 'info';
        await this.loadItems();
        if (!isBatch) this.selectedItemId = monitorItem.id || item.id;
        if (this.historyOpen && (!isBatch || String(this.selectedItemId || '') === String(monitorItem.id || ''))) {
          await this.loadHistory(monitorItem, true).catch(() => {});
        }
      } catch (error) {
        const message = error?.message || 'Monitoring check failed.';
        this.checkStage = 'error';
        if (begin?.check_id && monitorItem?.id) {
          try {
            await this.apiPost('finish_check.php', {
              item_id: monitorItem.id,
              check_id: begin.check_id,
              expected_state_version: begin.execution_context?.state_version,
              execution_token: begin.execution_context?.execution_token,
              check_status: 'error',
              summary: message,
              error_message: message,
            });
          } catch {
            // Keep the original error visible; the next refresh/history view can expose any stuck running check.
          }
        }
        this.lastRunResult = {
          check_status: 'error',
          item_id: monitorItem?.id || item.id,
          summary: message,
          error_message: message,
        };
        outcome = { ok: false, item_id: monitorItem?.id || item.id, error: message };
        if (!isBatch) this.setFeedback(message, 'error');
        await this.loadItems().catch(() => {});
        if (this.historyOpen && (!isBatch || String(this.selectedItemId || '') === String(monitorItem?.id || ''))) {
          await this.loadHistory(monitorItem, true).catch(() => {});
        }
      } finally {
        if (typeof this.$refs.tool88Monitor?.clearMonitoringExecutionContext === 'function') {
          this.$refs.tool88Monitor.clearMonitoringExecutionContext();
        }
        if (isBatch) {
          this.currentCheck = null;
          this.tool88Progress = null;
        } else {
          window.setTimeout(() => {
            this.checkingAny = false;
            this.checkStage = 'idle';
            this.checkingItem = null;
            this.resetRunVisualMode();
          }, 350);
        }
      }
      return outcome;
    },
    async checkAllDue() {
      if (this.checkingAny || this.batchRun.active) return;
      const queue = this.dueItems.slice();
      if (!queue.length) return;

      this.clearCheckRunUiBeforeStart();
      this.resetRunVisualMode();
      this.batchRun = {
        active: true,
        total: queue.length,
        currentIndex: 0,
        completed: 0,
        failed: 0,
        skipped: 0,
        phase: 'starting',
        currentItemLabel: '',
        nextItemLabel: this.itemQueueLabel(queue[0]),
        startedAt: new Date().toISOString(),
        finishedAt: '',
      };
      this.checkingAny = true;
      this.checkStage = 'queueing';
      this.checkingItem = queue[0] || null;

      try {
        for (let index = 0; index < queue.length; index += 1) {
          const item = queue[index];
          const nextItem = queue[index + 1] || null;
          this.batchRun.currentIndex = index + 1;
          this.batchRun.phase = 'preparing';
          this.batchRun.currentItemLabel = this.itemQueueLabel(item);
          this.batchRun.nextItemLabel = this.itemQueueLabel(nextItem);
          this.checkingItem = item;
          this.checkStage = 'queueing';

          // Give Vue a rendering tick so the user sees that the next queued item is being prepared.
          // eslint-disable-next-line no-await-in-loop
          await this.sleep(120);

          // eslint-disable-next-line no-await-in-loop
          const outcome = await this.checkItem(item, { batch: true });
          if (outcome?.skipped) this.batchRun.skipped += 1;
          else if (outcome?.ok) this.batchRun.completed += 1;
          else this.batchRun.failed += 1;

          this.batchRun.phase = nextItem ? 'queueing_next' : 'finishing';
          this.batchRun.currentItemLabel = this.itemQueueLabel(item);
          this.batchRun.nextItemLabel = this.itemQueueLabel(nextItem);
          this.checkingItem = nextItem || item;
          this.checkStage = nextItem ? 'queueing_next' : 'done';

          // eslint-disable-next-line no-await-in-loop
          await this.sleep(nextItem ? 350 : 180);
        }

        this.batchRun.phase = 'finished';
        this.batchRun.finishedAt = new Date().toISOString();
        const finished = this.batchRun.completed + this.batchRun.failed + this.batchRun.skipped;
        const summary = `Finished checking ${finished} due item${finished === 1 ? '' : 's'} with Tool 88.`;
        this.setFeedback(this.batchRun.failed ? `${summary} ${this.batchRun.failed} need review because they errored.` : summary, this.batchRun.failed ? 'warning' : 'success');
      } finally {
        window.setTimeout(() => {
          this.checkingAny = false;
          this.checkStage = 'idle';
          this.checkingItem = null;
          this.resetBatchRun();
          this.resetRunVisualMode();
        }, 1200);
      }
    },
    async runTool88MonitorCheck(item, serverPayload = null) {
      const tool = this.$refs.tool88Monitor;
      if (!tool) throw new Error('Tool 88 monitor host is not ready.');
      if (typeof tool.caseBuddyCheckDormantMonitoringItem === 'function') {
        return tool.caseBuddyCheckDormantMonitoringItem(item, {
          serverPayload,
          maxHops: 1,
          stopAfterFirstMovement: true,
          monitorMode: true,
        });
      }
      if (typeof tool.caseBuddyLoadRowsForHopSolver !== 'function' || typeof tool.caseBuddyRunNextAction !== 'function') {
        throw new Error('Tool 88 embedded monitoring API is not available yet.');
      }
      const rows = serverPayload?.rows || this.buildTool88RowsForMonitorItem(item);
      const candidate = serverPayload?.candidate || this.buildTool88CandidateForMonitorItem(item);
      await tool.caseBuddyLoadRowsForHopSolver(rows, {
        selectedHop: candidate,
        includeRows: true,
        selectNextUnresolved: false,
        traceDirection: 'forward',
      });
      const opened = await tool.caseBuddyOpenSelectedHop(candidate, { includeRows: true, rowsAlreadyLoaded: true });
      const action = await tool.caseBuddyRunNextAction({
        includeRows: true,
        forceCurrentRecommendation: true,
        ignoreReadyOutput: true,
        monitorMode: true,
        maxHops: 1,
        maxActions: 1,
        stopAfterFirstMovement: true,
        maxAutoLowValue: 0,
        pauseOnUnverifiedOutput: false,
        allowUnverifiedTraceOutput: true,
        acceptUnverifiedTraceOutput: true,
      });
      return { opened, action };
    },
    interpretTool88MonitorResult(item, result) {
      const snapshot = result?.action || result || {};
      const rows = Array.isArray(snapshot.outputRows) ? snapshot.outputRows : [];
      const sourceHash = String(item.transaction_hash || '').toLowerCase();
      const monitorAddress = String(item.monitor_address || '').toLowerCase();
      const movementRows = rows.filter((row) => {
        const hash = String(row?.[COL.TX_HASH] || '').toLowerCase();
        const from = String(row?.[COL.FROM_ADDRESS] || '').toLowerCase();
        const fromHash = String(row?.[COL.FROM_HASH] || '').toLowerCase();
        return hash && hash !== sourceHash && (from === monitorAddress || fromHash === sourceHash);
      });
      if (movementRows.length) {
        const first = movementRows[0];
        const parsedMovement = this.parseAmountAndToken(first[COL.FUNDS_FOLLOWED] || first[COL.AMOUNT] || '', item.token_symbol);
        return {
          check_status: 'movement_found',
          summary: `Movement found: ${this.shortHash(first[COL.TX_HASH])} from ${this.shortHash(first[COL.FROM_ADDRESS])} to ${this.shortHash(first[COL.TO_ADDRESS])}.`,
          movement_hash: first[COL.TX_HASH] || '',
          movement_from_address: first[COL.FROM_ADDRESS] || '',
          movement_to_address: first[COL.TO_ADDRESS] || '',
          movement_utc: first[COL.DATE] || '',
          movement_amount: parsedMovement.amount,
          movement_token: parsedMovement.token,
          tool88_snapshot_json: result,
          tool88_output_rows_json: rows,
        };
      }
      if (snapshot.partialMovementFound || snapshot.dormantRemainingAmount) {
        return {
          check_status: 'needs_review',
          summary: snapshot.message || 'Tool 88 reported a possible partial movement without a concrete transaction row. Review is required.',
          dormant_remaining_amount: snapshot.dormantRemainingAmount || null,
          tool88_snapshot_json: result,
          tool88_output_rows_json: rows,
        };
      }
      if (snapshot.needsManualReview || snapshot.guardrail) {
        return {
          check_status: 'needs_review',
          summary: snapshot.message || 'Tool 88 needs review before this monitoring item can be classified.',
          tool88_snapshot_json: result,
          tool88_output_rows_json: rows,
        };
      }
      return {
        check_status: 'no_movement',
        summary: 'No movement found beyond the monitored dormant transaction.',
        tool88_snapshot_json: result,
        tool88_output_rows_json: rows,
      };
    },
    async toggleHistory() {
      this.historyOpen = !this.historyOpen;
      if (this.historyOpen && this.selectedItem) {
        await this.loadHistory(this.selectedItem);
      }
    },
    async loadHistory(item = this.selectedItem, force = false) {
      if (!item?.id) return;
      if (!force && this.historyItemId === item.id && this.historyItems.length) return;
      this.historyLoading = true;
      try {
        const json = await this.apiGet('get_check_history.php', { item_id: item.id });
        this.historyItems = this.normalizeHistoryItems(json);
        this.historyItemId = item.id;
      } catch (error) {
        this.historyItems = [];
        this.historyItemId = item.id;
        this.setFeedback(error?.message || 'Could not load check history for this item.', 'error');
      } finally {
        this.historyLoading = false;
      }
    },
    normalizeHistoryItems(json = {}) {
      const raw = json.checks || json.history || json.items || json.rows || [];
      return Array.isArray(raw) ? raw : [];
    },
    buildFinishedCheckMessage(result = {}) {
      const status = result.check_status || result.status || '';
      const base = result.summary || 'Monitoring check completed.';
      if (['movement_found', 'partial_movement_found'].includes(status)) {
        const note = result.notification_sent
          ? ' Discord alert sent.'
          : (result.notification_error ? ` Discord alert was not sent: ${result.notification_error}` : '');
        return `${base}${note}`;
      }
      if (status === 'no_movement') return base || 'No movement found; item remains dormant.';
      return base;
    },
    focusLastRunItem() {
      if (!this.lastRunResult?.item_id) return;
      this.selectedItemId = this.lastRunResult.item_id;
    },
    async openSelectedHistory() {
      this.historyOpen = true;
      if (this.selectedItem) await this.loadHistory(this.selectedItem, true);
    },
    openFiltersFromUtility() {
      this.showFilters = true;
      this.closeUtility();
    },
    openManualAddFromUtility() {
      this.showManualAdd = true;
      this.closeUtility();
    },
    progressText(payload) {
      if (!payload) return '';
      if (typeof payload === 'string') return payload;
      return payload.message || payload.status || payload.phase || payload.step || payload.action || '';
    },
    async copySelectedSummary() {
      if (!this.selectedItem) return;
      const item = this.selectedItem;
      const lines = [
        `Address Monitoring: ${item.label || item.case_name || 'Dormant funds item'}`,
        `Status: ${this.formatStatus(item.status)}`,
        `Transaction: ${item.transaction_hash || ''}`,
        `Monitor address: ${item.monitor_address || ''}`,
        `Amount monitored: ${this.formatAmount(item.monitor_funds_amount, item.token_symbol)}`,
        `Hop logic: ${item.hop_logic || 'FIFO'}`,
        `Last checked: ${item.last_checked_at_utc || item.last_checked_at ? this.formatDateTime(item.last_checked_at_utc || item.last_checked_at) : 'Never'}`,
        this.isMovementStatus(item) ? `Movement alert acknowledged: ${this.isAcknowledgedMovement(item) ? this.formatDateTime(this.alertAcknowledgedAt(item)) : 'No'}` : '',
        item.last_summary ? `Last result: ${item.last_summary}` : '',
      ].filter(Boolean).join('\n');
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(lines);
          this.setFeedback('Monitoring summary copied.', 'success');
          return;
        }
      } catch {
        // Fall through to prompt fallback.
      }
      window.prompt('Copy monitoring summary', lines);
    },
    async toggleSelectedDisabled() {
      if (!this.selectedItem) return;
      await this.apiPost('update_item.php', {
        item_id: this.selectedItem.id,
        status: this.selectedItem.status === 'disabled' ? 'dormant' : 'disabled',
      });
      await this.loadItems();
    },
    async deleteSelected() {
      if (!this.selectedItem) return;
      if (!confirm('Delete this monitoring item?')) return;
      await this.apiPost('delete_item.php', { item_id: this.selectedItem.id });
      this.selectedItemId = null;
      await this.loadItems();
    },
    handleTool88Progress(payload) {
      this.tool88Progress = payload;
    },
    handleTool88Result(payload) {
      this.tool88Progress = payload;
    },
    handleTool88Guardrail(payload) {
      this.tool88Progress = payload;
    },
    shortHash(value) {
      const text = String(value || '');
      if (text.length <= 18) return text || '—';
      return `${text.slice(0, 8)}…${text.slice(-6)}`;
    },
    isMovementStatus(itemOrStatus) {
      const status = typeof itemOrStatus === 'string' ? itemOrStatus : itemOrStatus?.status;
      return ['movement_found', 'partial_movement_found'].includes(status);
    },
    alertAcknowledgedAt(item) {
      return item?.alert_acknowledged_at_utc
        || item?.alert_acknowledged_at
        || item?.movement_alert_acknowledged_at_utc
        || item?.movement_alert_acknowledged_at
        || item?.movement_acknowledged_at_utc
        || item?.movement_acknowledged_at
        || '';
    },
    isAcknowledgedMovement(item) {
      return this.isMovementStatus(item) && Boolean(
        this.alertAcknowledgedAt(item)
        || item?.movement_alert_acknowledged === true
        || item?.movement_alert_acknowledged === 1
        || item?.movement_alert_acknowledged === '1'
      );
    },
    isUnacknowledgedMovement(item) {
      return this.isMovementStatus(item) && !this.isAcknowledgedMovement(item);
    },
    movementStatusLabel(item) {
      if (!this.isMovementStatus(item)) return this.formatStatus(item?.status || item);
      return this.isAcknowledgedMovement(item)
        ? `${this.formatStatus(item.status)} · acknowledged`
        : `${this.formatStatus(item.status)} · alert`;
    },
    movementAlertTimeMs(item) {
      const candidates = [
        this.parseUtcDateMs(item?.last_movement_utc),
        this.parseUtcDateMs(item?.last_checked_at_utc || item?.last_checked_at),
        this.parseUtcDateMs(item?.updated_at_utc || item?.updated_at),
        this.parseUtcDateMs(item?.created_at_utc || item?.created_at),
      ].filter((value) => Number.isFinite(value));
      return candidates.length ? Math.max(...candidates) : 0;
    },
    reviewFirstMovementAlert() {
      const item = this.unacknowledgedMovementItems[0];
      if (!item) return;
      this.statusFilter = 'movement_unacknowledged';
      this.selectedItemId = item.id;
    },
    async acknowledgeSelectedMovementAlert() {
      if (!this.selectedItem || !this.isUnacknowledgedMovement(this.selectedItem)) return;
      if (this.isItemActivelyChecking(this.selectedItem)) {
        this.setFeedback('That item is being checked right now. Wait for its run to finish before acknowledging it.', 'info');
        return;
      }
      this.acknowledgementSaving = true;
      try {
        await this.apiPost('acknowledge_alert.php', {
          item_id: this.selectedItem.id,
          acknowledged: true,
          note: 'Acknowledged from Address Monitoring page.',
        });
        const keepId = this.selectedItem.id;
        await this.loadItems();
        this.selectedItemId = keepId;
        this.setFeedback('Movement alert acknowledged. The item is still stored, but it will no longer be pinned as an outstanding alert.', 'success');
      } catch (error) {
        this.setFeedback(error?.message || 'Could not acknowledge the movement alert.', 'error');
      } finally {
        this.acknowledgementSaving = false;
      }
    },
    async reopenSelectedMovementAlert() {
      if (!this.selectedItem || !this.isAcknowledgedMovement(this.selectedItem)) return;
      if (this.isItemActivelyChecking(this.selectedItem)) {
        this.setFeedback('That item is being checked right now. Wait for its run to finish before reopening it.', 'info');
        return;
      }
      this.acknowledgementSaving = true;
      try {
        await this.apiPost('acknowledge_alert.php', {
          item_id: this.selectedItem.id,
          acknowledged: false,
        });
        const keepId = this.selectedItem.id;
        await this.loadItems();
        this.selectedItemId = keepId;
        this.statusFilter = 'movement_unacknowledged';
        this.setFeedback('Movement alert reopened and returned to the outstanding alert queue.', 'success');
      } catch (error) {
        this.setFeedback(error?.message || 'Could not reopen the movement alert.', 'error');
      } finally {
        this.acknowledgementSaving = false;
      }
    },
    formatStatus(status) {
      return String(status || '').replace(/_/g, ' ') || 'unknown';
    },
    formatCreatedFrom(value) {
      const labels = {
        case_buddy: 'Case Buddy',
        manual_solve: 'Manual Solve',
        dormant_deliverable: 'Dormant deliverable',
        what_went_where: 'What Went Where',
        manual_advanced: 'Manual/admin add',
        legacy_tool61: 'Legacy Tool 61',
      };
      return labels[value] || this.formatStatus(value || 'unknown');
    },
    formatMonitorKind(value) {
      const labels = {
        full_dormant_path_ending: 'Full dormant path ending',
        partial_dormant_remainder: 'Partial dormant remainder',
        manual_advanced: 'Manual advanced item',
      };
      return labels[value] || this.formatStatus(value || 'monitor item');
    },
    formatAmount(amount, token = '') {
      const numeric = Number(String(amount ?? '').replace(/,/g, ''));
      if (!Number.isFinite(numeric)) return `${amount || '—'} ${token || ''}`.trim();
      const stable = ['USDT', 'USDC', 'USDD', 'DAI'].includes(String(token || '').toUpperCase());
      const decimals = stable ? 2 : (Math.abs(numeric) > 0 && Math.abs(numeric) < 0.0001 ? 8 : 4);
      return `${numeric.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${token || ''}`.trim();
    },
    parseUtcDateMs(value) {
      if (!value) return NaN;
      const text = String(value).trim();
      if (!text) return NaN;
      const iso = /Z$|[+-]\d{2}:?\d{2}$/.test(text) ? text : `${text.replace(' ', 'T')}Z`;
      return Date.parse(iso);
    },
    parseAmountAndToken(value, fallbackToken = '') {
      const text = String(value ?? '').replace(/,/g, '').trim();
      const match = text.match(/^-?\d+(?:\.\d+)?/);
      const amount = match ? match[0] : '';
      const token = text.slice(amount.length).trim() || fallbackToken || '';
      return { amount, token };
    },
    nextCheckLabel(item) {
      if (!item) return '';
      if (this.isUnacknowledgedMovement(item)) return 'Movement found · review alert';
      if (this.isAcknowledgedMovement(item)) return 'Movement found · acknowledged';
      if (['disabled', 'deleted'].includes(item.status)) return this.formatStatus(item.status);
      const nextValue = item.next_check_after_utc || item.next_check_after;
      return nextValue && !this.isItemDue(item) ? `Available ${this.formatDateTime(nextValue)}` : 'Ready to check';
    },
    formatDateTime(value) {
      if (!value) return '';
      const parsedMs = this.parseUtcDateMs(value);
      if (!Number.isFinite(parsedMs)) return String(value);
      return new Date(parsedMs).toLocaleString();
    },
  },
};
</script>

<style scoped>
.address-monitoring-page {
  max-width: 1260px;
  margin: 1.5rem auto;
  padding: 1rem;
  color: #0f172a;
}
.address-monitoring-page--embedded {
  max-width: none;
  margin: 0;
  padding: 0;
}
.address-monitoring-page__header,
.address-monitoring-page__section-header,
.address-monitoring-page__detail-header,
.address-monitoring-page__side-header,
.address-monitoring-page__actions,
.address-monitoring-page__secondary-actions,
.address-monitoring-page__result-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.address-monitoring-page__header {
  border: 1px solid #dbe7f3;
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  padding: 1rem;
}
.address-monitoring-page__hero-copy {
  max-width: 760px;
}
.address-monitoring-page__hero-copy h1,
.address-monitoring-page__side-header h2,
.address-monitoring-page__section-header h2,
.address-monitoring-page__detail-header h2,
.address-monitoring-page__result-banner h2 {
  margin: 0;
}
.address-monitoring-page__hero-copy h1 {
  font-size: clamp(1.55rem, 2.5vw, 2.25rem);
  letter-spacing: -0.035em;
}
.address-monitoring-page__subtitle,
.address-monitoring-page__detail-header p,
.address-monitoring-page__result-banner p,
.address-monitoring-page__section-header p,
.address-monitoring-page__notification-panel p,
.address-monitoring-page__advanced-panel p,
.address-monitoring-page__guide-content p,
.address-monitoring-page__latest-card p,
.address-monitoring-page__manual-help p {
  color: #475569;
  line-height: 1.5;
}
.address-monitoring-page__subtitle {
  margin: 0.35rem 0 0;
}
.address-monitoring-page__eyebrow {
  margin: 0 0 0.25rem;
  color: #2563eb;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.address-monitoring-page__chips {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-top: 0.7rem;
}
.address-monitoring-page__session-chip,
.address-monitoring-page__quiet-chip,
.address-monitor-card__status,
.address-monitor-card__due,
.address-monitor-card__alert-chip,
.address-monitor-card__ack-chip {
  border-radius: 999px;
  padding: 0.18rem 0.58rem;
  font-size: 0.72rem;
  font-weight: 900;
}
.address-monitoring-page__session-chip.is-authenticated {
  background: #dcfce7;
  color: #166534;
}
.address-monitoring-page__session-chip.is-guest,
.address-monitoring-page__quiet-chip {
  background: #f1f5f9;
  color: #475569;
}
.address-monitoring-page__embedded-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1e40af;
  padding: 0.28rem 0.6rem;
  font-size: 0.74rem;
  font-weight: 900;
}
.address-monitoring-page__button-link,
button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border: 1px solid #cbd5e1;
  border-radius: 11px;
  padding: 0.55rem 0.8rem;
  background: #fff;
  color: #0f172a;
  font-weight: 850;
  text-decoration: none;
  cursor: pointer;
}
button.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
button.primary.xl {
  min-height: 3.1rem;
  padding-inline: 1rem;
  font-size: 1rem;
}
button.secondary {
  background: #0f172a;
  border-color: #0f172a;
  color: #fff;
}
button.danger {
  color: #991b1b;
  border-color: #fecaca;
}
button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.address-monitoring-page__feedback,
.address-monitoring-page__mini-feedback {
  margin: 0.85rem 0;
  border-radius: 14px;
  padding: 0.75rem 0.9rem;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 750;
}
.address-monitoring-page__feedback.is-success,
.address-monitoring-page__mini-feedback.is-success {
  background: #dcfce7;
  color: #166534;
}
.address-monitoring-page__feedback.is-error,
.address-monitoring-page__mini-feedback.is-error {
  background: #fee2e2;
  color: #991b1b;
}
.address-monitoring-page__feedback.is-warning,
.address-monitoring-page__mini-feedback.is-warning {
  background: #fffbeb;
  color: #92400e;
}
.address-monitoring-page__batch-strip {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  margin: 1rem 0;
  padding: 0.9rem 1rem;
  border: 1px solid #bfdbfe;
  border-radius: 18px;
  background: linear-gradient(135deg, #eff6ff, #f8fafc);
  box-shadow: 0 18px 42px rgba(37, 99, 235, 0.10);
}
.address-monitoring-page__batch-copy h2,
.address-monitoring-page__batch-copy p {
  margin: 0.18rem 0;
}
.address-monitoring-page__batch-spinner {
  position: relative;
  display: grid;
  place-items: center;
  width: 2.45rem;
  height: 2.45rem;
}
.address-monitoring-page__batch-spinner span {
  position: absolute;
  border: 2px solid #2563eb;
  border-radius: 999px;
  opacity: 0.85;
  animation: address-monitor-batch-ripple 1.35s ease-out infinite;
}
.address-monitoring-page__batch-spinner span:nth-child(2) {
  animation-delay: 0.42s;
}
.address-monitoring-page__batch-count {
  display: grid;
  place-items: center;
  min-width: 4.5rem;
  padding: 0.5rem 0.7rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.78);
  color: #1e3a8a;
  font-weight: 850;
}
.address-monitoring-page__batch-count strong {
  font-size: 1.35rem;
  line-height: 1;
}
.address-monitoring-page__batch-count span {
  color: #64748b;
  font-size: 0.76rem;
}
.address-monitoring-page__batch-progress {
  overflow: hidden;
  height: 0.45rem;
  margin-top: 0.55rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.28);
}
.address-monitoring-page__batch-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #2563eb;
  transition: width 220ms ease;
}
.address-monitoring-page__batch-inline {
  margin: 0.35rem 0 0;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 750;
}
@keyframes address-monitor-batch-ripple {
  0% { width: 0.7rem; height: 0.7rem; opacity: 0.9; }
  100% { width: 2.45rem; height: 2.45rem; opacity: 0; }
}
.address-monitoring-page__movement-alert-strip {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: center;
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid #fecaca;
  border-radius: 18px;
  background: linear-gradient(135deg, #fff1f2, #fff7ed);
  box-shadow: 0 18px 45px rgba(225, 29, 72, 0.12);
  animation: address-monitor-alert-pulse 1.8s ease-in-out infinite;
}
.address-monitoring-page__movement-alert-strip h2,
.address-monitoring-page__movement-alert-strip p {
  margin: 0.2rem 0;
}
.address-monitoring-page__movement-alert-icon {
  display: grid;
  place-items: center;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  background: #be123c;
  color: #fff;
  font-weight: 1000;
}
@keyframes address-monitor-alert-pulse {
  0%, 100% { box-shadow: 0 18px 45px rgba(225, 29, 72, 0.10); }
  50% { box-shadow: 0 18px 45px rgba(225, 29, 72, 0.22); }
}
.address-monitoring-page__result-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0;
  border: 1px solid #dbe7f3;
  border-radius: 18px;
  background: #f8fafc;
  padding: 1rem;
}
.address-monitoring-page__result-banner.is-movement {
  border-color: #fecaca;
  background: #fff1f2;
}
.address-monitoring-page__result-banner.is-attention {
  border-color: #fde68a;
  background: #fffbeb;
}
.address-monitoring-page__result-banner.is-quiet {
  border-color: #bfdbfe;
  background: #eff6ff;
}
.address-monitoring-page__result-note {
  font-weight: 850;
}
.address-monitoring-page__summary-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  margin: 1rem 0;
}
.address-monitoring-page__summary-strip article,
.address-monitoring-page__list-panel,
.address-monitoring-page__detail-panel,
.address-monitoring-page__manual-panel,
.address-monitoring-page__manual-help,
.address-monitoring-page__latest-card,
.address-monitoring-page__basis-card,
.address-monitoring-page__history-card,
.address-monitor-card,
.address-monitoring-page__filters,
.address-monitoring-page__quick-bar {
  border: 1px solid #dbe7f3;
  border-radius: 18px;
  background: #fff;
}
.address-monitoring-page__summary-strip article {
  padding: 0.8rem;
}
.address-monitoring-page__summary-strip span,
.address-monitoring-page__detail-grid dt,
.address-monitoring-page__basis-grid dt,
.address-monitoring-page__filters span,
.address-monitoring-page__manual-form span,
.address-monitoring-page__notification-panel label span {
  display: block;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 850;
  text-transform: uppercase;
}
.address-monitoring-page__summary-strip strong {
  display: block;
  margin-top: 0.12rem;
  font-size: 1.45rem;
}
.address-monitoring-page__summary-strip small {
  display: block;
  color: #64748b;
  font-weight: 800;
  margin-top: 0.15rem;
}
.address-monitoring-page__summary-strip article.has-alert {
  border-color: #fecaca;
  background: #fff1f2;
  box-shadow: 0 0 0 1px rgba(225, 29, 72, 0.08), 0 14px 32px rgba(225, 29, 72, 0.08);
}
.address-monitoring-page__quick-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.55rem;
  padding: 0.65rem;
  margin-bottom: 0.75rem;
}
.address-monitoring-page__quick-bar input,
.address-monitoring-page__filters input,
.address-monitoring-page__filters select,
.address-monitoring-page__manual-form input,
.address-monitoring-page__manual-form select,
.address-monitoring-page__manual-form textarea,
.address-monitoring-page__notification-panel input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 11px;
  padding: 0.55rem 0.65rem;
  color: #0f172a;
  background: #fff;
}
.address-monitoring-page__filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) repeat(4, auto);
  gap: 0.65rem;
  align-items: end;
  margin-bottom: 0.9rem;
  padding: 0.75rem;
}
.address-monitoring-page__layout {
  display: grid;
  grid-template-columns: minmax(330px, 0.9fr) minmax(430px, 1.1fr);
  gap: 1rem;
  align-items: start;
}
.address-monitoring-page__list-panel,
.address-monitoring-page__detail-panel,
.address-monitoring-page__manual-panel {
  padding: 0.8rem;
}
.address-monitoring-page__list-meta {
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 800;
  margin-bottom: 0.6rem;
}
.address-monitoring-page__item-list {
  display: grid;
  gap: 0.55rem;
  max-height: min(68vh, 720px);
  overflow: auto;
  padding-right: 0.2rem;
}
.address-monitor-card {
  display: grid;
  gap: 0.28rem;
  width: 100%;
  padding: 0.75rem;
  text-align: left;
}
.address-monitor-card.selected {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
}
.address-monitor-card.is-due {
  border-color: #bfdbfe;
}
.address-monitor-card.is-unacknowledged-movement {
  border-color: #fecaca;
  background: #fff7f7;
  box-shadow: inset 4px 0 0 #e11d48;
}
.address-monitor-card.is-acknowledged-movement {
  opacity: 0.88;
}
.address-monitor-card__topline {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.address-monitor-card__status {
  width: fit-content;
  background: #e2e8f0;
  color: #334155;
  font-size: 0.68rem;
  text-transform: uppercase;
}
.address-monitor-card__status.is-dormant,
.address-monitor-card__status.is-no_movement {
  background: #dbeafe;
  color: #1e40af;
}
.address-monitor-card__status.is-movement_found,
.address-monitor-card__status.is-partial_movement_found {
  background: #fee2e2;
  color: #991b1b;
}
.address-monitor-card__status.is-needs_review,
.address-monitor-card__status.is-error {
  background: #fef3c7;
  color: #92400e;
}
.address-monitor-card__status.is-disabled {
  background: #e5e7eb;
  color: #374151;
}
.address-monitor-card__due,
.address-monitor-card__alert-chip,
.address-monitor-card__ack-chip {
  font-size: 0.68rem;
  text-transform: uppercase;
}
.address-monitor-card__due {
  background: #dcfce7;
  color: #166534;
}
.address-monitor-card__alert-chip {
  background: #fee2e2;
  color: #991b1b;
}
.address-monitor-card__ack-chip {
  background: #dcfce7;
  color: #166534;
}
.address-monitor-card small,
.address-monitor-card span:not(.address-monitor-card__status):not(.address-monitor-card__due):not(.address-monitor-card__alert-chip):not(.address-monitor-card__ack-chip) {
  color: #64748b;
}
.address-monitoring-page__detail-panel {
  position: sticky;
  top: 1rem;
}
.address-monitoring-page__detail-header p {
  margin: 0.2rem 0 0;
  font-size: 0.86rem;
  font-weight: 700;
}
.address-monitoring-page__detail-status-stack {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: flex-end;
}
.address-monitoring-page__latest-card {
  margin-top: 0.85rem;
  padding: 0.85rem;
  background: #f8fafc;
}
.address-monitoring-page__latest-card.is-movement {
  border-color: #fecaca;
  background: #fff1f2;
}
.address-monitoring-page__latest-card.is-attention {
  border-color: #fde68a;
  background: #fffbeb;
}
.address-monitoring-page__latest-card.is-quiet {
  border-color: #bfdbfe;
  background: #eff6ff;
}
.address-monitoring-page__latest-card.is-acknowledged {
  border-color: #bbf7d0;
  background: #f0fdf4;
}
.address-monitoring-page__ack-line {
  margin-top: 0.45rem;
  color: #166534;
  font-weight: 800;
}
.address-monitoring-page__ack-line.strong {
  color: #991b1b;
}
.address-monitoring-page__primary-check-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.9rem 0;
  flex-wrap: wrap;
}
.address-monitoring-page__primary-check-row span {
  color: #64748b;
  font-weight: 800;
}
.address-monitoring-page__detail-grid,
.address-monitoring-page__basis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.address-monitoring-page__detail-grid dd,
.address-monitoring-page__basis-grid dd {
  margin: 0.15rem 0 0;
  word-break: break-word;
}
.address-monitoring-page__secondary-actions {
  justify-content: flex-start;
  margin-top: 0.85rem;
}
.address-monitoring-page__basis-card,
.address-monitoring-page__history-card {
  margin-top: 1rem;
  padding: 0.85rem;
  background: #f8fbff;
}
.address-monitoring-page__basis-card summary {
  font-weight: 900;
  cursor: pointer;
}
.address-monitoring-page__basis-card pre,
.address-monitoring-page__manual-help pre,
.address-monitoring-page__debug pre {
  max-height: 260px;
  overflow: auto;
  background: #0f172a;
  color: #dbeafe;
  border-radius: 12px;
  padding: 0.85rem;
}
.address-monitoring-page__history-list {
  display: grid;
  gap: 0.6rem;
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;
}
.address-monitoring-page__history-list li {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.65rem;
  align-items: start;
  border-top: 1px solid #e2e8f0;
  padding-top: 0.6rem;
}
.address-monitoring-page__history-list small {
  display: block;
  margin-top: 0.2rem;
  color: #64748b;
}
.address-monitoring-page__manual-panel {
  margin: 1rem 0;
  background: #f8fbff;
}
.address-monitoring-page__manual-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 1rem;
  align-items: start;
  margin-top: 0.9rem;
}
.address-monitoring-page__manual-form {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
}
.address-monitoring-page__manual-form label,
.address-monitoring-page__notification-panel label {
  display: grid;
  gap: 0.28rem;
}
.address-monitoring-page__manual-wide,
.address-monitoring-page__manual-actions {
  grid-column: 1 / -1;
}
.address-monitoring-page__manual-actions {
  display: flex;
  gap: 0.6rem;
}
.address-monitoring-page__manual-help {
  padding: 0.85rem;
}
.address-monitoring-page__side-backdrop,
.address-monitoring-page__checking-overlay {
  position: fixed;
  inset: 0;
  z-index: 5000;
  background: rgba(15, 23, 42, 0.42);
}
.address-monitoring-page__side-backdrop {
  display: flex;
  justify-content: flex-end;
}
.address-monitoring-page__side-panel {
  width: min(560px, calc(100vw - 28px));
  height: 100%;
  overflow: auto;
  background: #fff;
  box-shadow: -20px 0 50px rgba(15, 23, 42, 0.24);
  padding: 1rem;
}
.address-monitoring-page__side-tabs {
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
  flex-wrap: wrap;
}
.address-monitoring-page__side-tabs button.active,
.address-monitoring-page__guide-tabs button.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}
.address-monitoring-page__guide-tabs {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}
.address-monitoring-page__guide-content details,
.address-monitoring-page__notification-state,
.address-monitoring-page__status-guide article,
.address-monitoring-page__advanced-panel button {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
  padding: 0.75rem;
  margin-bottom: 0.6rem;
}
.address-monitoring-page__guide-content summary {
  font-weight: 900;
  cursor: pointer;
}
.address-monitoring-page__status-guide {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
}
.address-monitoring-page__notification-panel,
.address-monitoring-page__advanced-panel {
  display: grid;
  gap: 0.75rem;
}
.address-monitoring-page__notification-state {
  display: grid;
  gap: 0.2rem;
}
.address-monitoring-page__checking-overlay {
  display: grid;
  place-items: center;
  padding: 1rem;
}
.address-monitoring-page__checking-card {
  width: min(520px, 100%);
  border-radius: 24px;
  background: #fff;
  padding: 1.25rem;
  text-align: center;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.28);
}
.address-monitoring-page__checking-card-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.35rem;
}
.address-monitoring-page__checking-card-actions button {
  font-size: 0.78rem;
  padding: 0.38rem 0.7rem;
}
.address-monitoring-page__run-lock-note {
  color: #64748b;
  font-size: 0.82rem;
  margin: 0.35rem 0 0;
}
.address-monitoring-page__runner-dock {
  position: fixed;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4990;
  width: min(340px, calc(100vw - 2rem));
  border: 1px solid #bfdbfe;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.22);
  padding: 0.95rem;
  backdrop-filter: blur(14px);
}
.address-monitoring-page__runner-dock-top {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.7rem;
  align-items: center;
}
.address-monitoring-page__runner-dock-top h2 {
  margin: 0.1rem 0 0;
  font-size: 1rem;
}
.address-monitoring-page__runner-dock-pulse {
  width: 2.45rem;
  height: 2.45rem;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: #eff6ff;
}
.address-monitoring-page__runner-dock-pulse span {
  width: 1.05rem;
  height: 1.05rem;
  border-radius: 999px;
  background: #2563eb;
  box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.42);
  animation: monitor-pulse 1.35s ease-out infinite;
}
@keyframes monitor-pulse {
  70% { box-shadow: 0 0 0 14px rgba(37, 99, 235, 0); }
  100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
}
.address-monitoring-page__runner-dock-item {
  margin: 0.7rem 0 0.3rem;
  font-weight: 900;
  color: #0f172a;
  word-break: break-word;
}
.address-monitoring-page__runner-dock-status,
.address-monitoring-page__runner-dock-lock {
  margin: 0.35rem 0;
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.35;
}
.address-monitoring-page__runner-dock-lock {
  color: #64748b;
}
.address-monitoring-page__runner-dock-progress {
  height: 0.45rem;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
  margin: 0.75rem 0;
}
.address-monitoring-page__runner-dock-progress span {
  display: block;
  height: 100%;
  min-width: 8%;
  border-radius: inherit;
  background: #2563eb;
  transition: width 0.25s ease;
}
.address-monitoring-page__runner-dock-actions {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
}
.address-monitoring-page__runner-dock-actions button {
  flex: 1 1 auto;
  padding: 0.46rem 0.7rem;
  font-size: 0.8rem;
}
.address-monitoring-page__orbit {
  position: relative;
  width: 84px;
  height: 84px;
  margin: 0 auto 1rem;
  border: 3px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 999px;
  animation: monitor-spin 1s linear infinite;
}
.address-monitoring-page__orbit span {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #2563eb;
}
.address-monitoring-page__orbit span:nth-child(1) { top: 4px; left: 50%; }
.address-monitoring-page__orbit span:nth-child(2) { right: 8px; bottom: 14px; }
.address-monitoring-page__orbit span:nth-child(3) { left: 8px; bottom: 14px; }
@keyframes monitor-spin { to { transform: rotate(360deg); } }
.address-monitoring-page__check-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin: 1rem 0;
  padding: 0;
  list-style: none;
}
.address-monitoring-page__check-steps li {
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  padding: 0.35rem 0.5rem;
  font-size: 0.78rem;
  font-weight: 900;
}
.address-monitoring-page__check-steps li.active {
  background: #dbeafe;
  color: #1e40af;
}
.address-monitoring-page__check-steps li.done {
  background: #dcfce7;
  color: #166534;
}
.address-monitoring-page__progress-text {
  color: #475569;
  font-size: 0.85rem;
}
.address-monitoring-page__tool88-host {
  position: fixed;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
.address-monitoring-page__empty {
  padding: 1.25rem;
  color: #64748b;
  text-align: center;
}
.address-monitoring-page__debug {
  margin-top: 1rem;
}
code {
  word-break: break-all;
}
@media (max-width: 980px) {
  .address-monitoring-page__movement-alert-strip {
    grid-template-columns: 1fr;
  }
  .address-monitoring-page__movement-alert-icon {
    width: 2rem;
    height: 2rem;
  }
  .address-monitoring-page__summary-strip,
  .address-monitoring-page__layout,
  .address-monitoring-page__filters,
  .address-monitoring-page__manual-layout,
  .address-monitoring-page__manual-form,
  .address-monitoring-page__detail-grid,
  .address-monitoring-page__basis-grid {
    grid-template-columns: 1fr;
  }
  .address-monitoring-page__batch-strip {
    grid-template-columns: 1fr;
  }
  .address-monitoring-page__runner-dock {
    left: 0.75rem;
    right: 0.75rem;
    top: auto;
    bottom: 0.75rem;
    transform: none;
    width: auto;
  }
  .address-monitoring-page__batch-spinner,
  .address-monitoring-page__batch-count {
    justify-self: start;
  }
  .address-monitoring-page__detail-panel {
    position: static;
  }
  .address-monitoring-page__quick-bar {
    grid-template-columns: 1fr;
  }
}


.address-monitoring-page__manual-tsv-card {
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid #dbeafe;
  border-radius: 18px;
  background: #f8fbff;
  display: grid;
  gap: 0.85rem;
}

.address-monitoring-page__manual-tsv-card h3 {
  margin: 0;
  font-size: 1rem;
}

.address-monitoring-page__manual-tsv-card p {
  margin: 0.25rem 0 0;
  color: #64748b;
}

.address-monitoring-page__manual-tsv-card label {
  display: grid;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #334155;
}

.address-monitoring-page__manual-tsv-card textarea {
  width: 100%;
  min-height: 6rem;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 0.7rem 0.8rem;
  font: inherit;
  resize: vertical;
  background: #ffffff;
  color: #0f172a;
}

.address-monitoring-page__manual-tsv-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.address-monitoring-page__manual-tsv-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.5rem;
}

.address-monitoring-page__manual-tsv-summary span {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  padding: 0.55rem 0.65rem;
  color: #334155;
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.address-monitoring-page__manual-tsv-summary strong {
  display: block;
  color: #0f172a;
  margin-bottom: 0.15rem;
}

</style>
